import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  X, User, Mail, Calendar, Save, Plus, Edit, Trash2, Search, Filter, ChevronLeft, ChevronRight,
  FileText, FileSpreadsheet, ArrowUp, ArrowDown, Settings, Minus, AlertTriangle, BarChart3,
  ArrowDownNarrowWide, ArrowUpNarrowWide, Clock, MapPin, Users, Building2, FolderOpen, Briefcase, Shield
} from 'lucide-react';
import { getAllEmployees, deleteEmployee, registerEmployee, updateEmployee, getEmployeeImage } from '../services/HRstaff';
import { getAllProjects } from '../services/projectManagerAPIs';
import { getFlaggedEmployeesByFeedback, getEmployeeNotes, getViolationsOverview } from '../services/NoteAPIs';
import ShortProfile from './ShortViewProfile';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import EmployeeAbsenceReport, { EmployeeFeedbackReportContent, BulkAbsenceExportDialog } from '../reports/EmployeeFeedbackReport';
import SimpleAbsenceReportContent from '../reports/SimpleAbsenceReport';
import { useTheme } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend
} from 'chart.js';

import PageHeader from './headers/Header';
import marabesLogo from '../assets/images/logos/Marabes.png';
import { useOffices } from '../offices';

ChartJS.register(CategoryScale, LinearScale, BarElement, ChartTooltip, ChartLegend);

if (typeof jsPDF === 'function' && !jsPDF.prototype.autoTable) {
  jsPDF.prototype.autoTable = autoTable;
}

const getBase64FromUrl = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.setAttribute('crossOrigin', 'anonymous');
    img.src = url;
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => {
      resolve(null);
    };
  });
};

const ANIMATION_DELAY_MS = 300;
const EMPLOYEES_PER_PAGE = 10;
const IMAGE_BASE_URL = "https://axopeople.blob.core.windows.net/images/";
const THEME_PRIMARY = '#0EA5E9';
const THEME_SECONDARY = '#0284C7';

// FTE options
const FTE_OPTIONS = [0.25, 0.5, 0.75, 1.0];

// Position options
const POSITION_OPTIONS = [
  'Account Manager', 'Bookkeeper', 'Client Adviser', 'Customer Service',
  'Developer', 'Director', 'Facility', 'HR', 'HR Manager', 'IT Professional',
  'Manager', 'Marketing', 'Personal Assistance', 'Planner', 'Project Manager',
  'Quality Reviewer', 'Software Engineer', 'HQ'
];

const projectOptions = [
  "N/A", "MM", "LC", "IT", "FF", "FL", "MKT", "HA", "CS", "HR", "S", "H2D", "WLF", "HBL", "MC", "TEDS", "QR", "MA", "PL", "WAL", "SIPI", "Lineo", "CB", "UTB", "AP", "AXP", "ENTRPNR"
];

const calculateAge = (dob) => {
  if (!dob) return 'N/A';
  const birthDate = new Date(dob);
  if (isNaN(birthDate.getTime())) return 'N/A';
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age >= 0 ? age : 'N/A';
};

function Employees() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { offices: rawOffices } = useOffices();

  const getIntParam = (param, fallback = 1) => {
    const val = parseInt(searchParams.get(param), 10);
    return isNaN(val) ? fallback : Math.max(1, val);
  };

  // State
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('q') || '');
  const [selectedDepartment, setSelectedDepartment] = useState(searchParams.get('dept') || 'All');
  const [selectedOffice, setSelectedOffice] = useState(searchParams.get('office') || 'All');
  const [selectedJob, setSelectedJob] = useState(searchParams.get('job') || 'All');
  const [selectedCompany, setSelectedCompany] = useState(searchParams.get('company') || 'All');
  const [selectedGender, setSelectedGender] = useState(searchParams.get('gender') || 'All');
  const [selectedRole, setSelectedRole] = useState(searchParams.get('role') || 'All');
  const [selectedProject, setSelectedProject] = useState(searchParams.get('project') || 'All');
  const [selectedViolations, setSelectedViolations] = useState(searchParams.get('violations') || 'All');
  const [selectedAgeFilter, setSelectedAgeFilter] = useState(searchParams.get('age') || 'all');
  const [showAgeDistributionModal, setShowAgeDistributionModal] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [activeTab, setActiveTab] = useState('personal');
  const [currentPage, setCurrentPage] = useState(getIntParam('page', 1));
  const [totalEmployees, setTotalEmployees] = useState(0);
  const [profileImages, setProfileImages] = useState({});
  const [flaggedEmployees, setFlaggedEmployees] = useState({});
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [savingEmployee, setSavingEmployee] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [createFormData, setCreateFormData] = useState({
    firstName: '', lastName: '', email: '', password: '', role: '', departmentName: '',
    dateOfJoining: '', profileImage: null,
    positions: [{ positionName: '', fte: 1.0 }],
    dob: '', phone: '', office: '', gender: '', cin: '', diploma: '', cnss: '',
    company: '', projectName: '', secondProjectName: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [newImageUploaded, setNewImageUploaded] = useState(false);
  const [isEditingInCreateForm, setIsEditingInCreateForm] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [exportColumns, setExportColumns] = useState({
    name: true, email: true, phone: true, age: true, positions: true, department: true,
    office: true, company: true, contractType: true, startDate: true,
    periodWorked: true, projectName: true, secondProjectName: true, gender: false,
  });
  const [showMoreProjects, setShowMoreProjects] = useState(false);
  const [selectedAdditionalProjects, setSelectedAdditionalProjects] = useState([]);
  const [feedbackReportEmployee, setFeedbackReportEmployee] = useState(null);
  const [showFeedbackReport, setShowFeedbackReport] = useState(false);
  const [showFullFeedbackReport, setShowFullFeedbackReport] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [showAuditInfo, setShowAuditInfo] = useState(false);

  // Options
  const departments = ['All', 'IT', 'HR', 'HQ', 'Planning', 'Marketing', 'Accounting', 'Customer_services', 'Billing', 'Facility'];
  const departmentOptions = [
    { label: 'All', value: 'All' },
    { label: 'IT', value: 'IT' },
    { label: 'HR', value: 'HR' },
    { label: 'HQ', value: 'HQ' },
    { label: 'Planning', value: 'Planning' },
    { label: 'Accounting', value: 'Accounting' },
    { label: 'Marketing', value: 'Marketing' },
    { label: 'Customer Services', value: 'Customer_services' },
    { label: 'Billing', value: 'Billing' },
    { label: 'Facility', value: 'Facility' },
  ];
  const offices = useMemo(() => {
    if (rawOffices && Array.isArray(rawOffices) && rawOffices.length > 0) {
      const names = rawOffices.map(o => (typeof o === 'string' ? o : o.name)).filter(Boolean);
      const uniqueNames = Array.from(new Set(names));
      return ['All', ...uniqueNames];
    }
    return ['All', 'Nador', 'Tangier', 'Selouane', 'Casablanca'];
  }, [rawOffices]);
  const contractTypes = ['CDI', 'ANAPEC', 'CDD', 'Internship', 'PSC'];
  const jobTitles = [
    'All', 'Director', 'HR Manager', 'Project Manager', 'Customer Service', 'HR',
    'Manager', 'Account Manager', 'Quality Reviewer', 'Bookkeeper',
    'Planner', 'Personal Assistance', 'Software Engineer', 'Marketing', 'Facility', 'Client Adviser', 'IT Professional'
  ];
  const roles = ['HR', 'EMPLOYEE', 'MANAGER', 'DIRECTOR', 'DEV'];
  const roleFilterOptions = ['All', 'EMPLOYEE', 'HR', 'MANAGER', 'DIRECTOR', 'DEV'];
  const genders = ['All', 'MALE', 'FEMALE'];
  const diplomaOptions = ['High School Diploma', 'Specialized Technician', 'Bachelor', 'Engineer', 'Master', 'PhD', 'N/A'];
  const companyOptions = ['MG', 'MC'];
  const companyFilterOptions = ['All', ...companyOptions];
  const projectFilterOptions = useMemo(() => {
    if (availableProjects && Array.isArray(availableProjects) && availableProjects.length > 0) {
      const names = availableProjects.map(p => typeof p === 'string' ? p : p.projectName).filter(Boolean);
      const uniqueNames = Array.from(new Set(names)).sort();
      return ['All', ...uniqueNames];
    }
    return ['All', ...projectOptions];
  }, [availableProjects, projectOptions]);

  // FTE Helpers
  const calculateTotalFTE = (positions) => {
    return positions.reduce((sum, pos) => sum + (parseFloat(pos.fte) || 0), 0);
  };

  const isFTEValid = (positions) => {
    const total = calculateTotalFTE(positions);
    return total > 0 && total <= 1.001;
  };

  // Effects
  useEffect(() => {
    if (isEditingInCreateForm && createFormData.secondProjectName && createFormData.secondProjectName !== 'N/A') {
      const projects = createFormData.secondProjectName.split('-').filter(p => p.trim() !== '');
      setSelectedAdditionalProjects(projects);
    }
  }, [isEditingInCreateForm, createFormData.secondProjectName]);

  useEffect(() => {
    if (selectedAdditionalProjects.length > 0) {
      const projectsString = selectedAdditionalProjects.join('-');
      setCreateFormData(prev => ({ ...prev, secondProjectName: projectsString }));
    } else {
      setCreateFormData(prev => ({ ...prev, secondProjectName: 'N/A' }));
    }
  }, [selectedAdditionalProjects]);

  const fetchSingleImage = useCallback(async (employeeId) => {
    try {
      if (!employeeId || profileImages[employeeId]) return null;
      const imageBlob = await getEmployeeImage(employeeId);
      if (imageBlob) return { employeeId, imageUrl: imageBlob };
      return null;
    } catch (error) {
      console.warn(`Failed to fetch image for employee ${employeeId}:`, error);
      return null;
    }
  }, [profileImages]);

  const fetchEmployeeImages = useCallback(async (employeeList) => {
    if (!employeeList?.length) return;
    const employeesToFetch = employeeList.filter(emp => emp.employeeId && !profileImages[emp.employeeId]);
    if (employeesToFetch.length === 0) return;
    try {
      const batchSize = 3;
      for (let i = 0; i < employeesToFetch.length; i += batchSize) {
        const batch = employeesToFetch.slice(i, i + batchSize);
        const batchPromises = batch.map(emp => fetchSingleImage(emp.employeeId));
        const batchResults = await Promise.allSettled(batchPromises);
        const successfulResults = batchResults
          .filter(result => result.status === 'fulfilled' && result.value)
          .map(result => result.value);
        if (successfulResults.length > 0) {
          setProfileImages(prev => {
            const updatedImages = { ...prev };
            successfulResults.forEach(res => {
              updatedImages[res.employeeId] = res.imageUrl;
            });
            return updatedImages;
          });
        }
      }
    } catch (err) {
      console.error('Unexpected error in fetchEmployeeImages:', err);
    }
  }, [profileImages, fetchSingleImage]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setLoading(true);
        setError(null);
        const [data, flagged, violationsData] = await Promise.all([
          getAllEmployees(),
          getFlaggedEmployeesByFeedback(3),
          getViolationsOverview(),
        ]);

        const totalNegativeNotes = {};
        if (violationsData && violationsData.employeeNotes) {
          violationsData.employeeNotes.forEach(note => {
            if (note.noteType === 'NEGATIVE') {
              totalNegativeNotes[note.employeeId] = (totalNegativeNotes[note.employeeId] || 0) + 1;
            }
          });
        }
        const formatDepartmentName = (dept) => {
          if (!dept || dept === 'N/A') return dept;
          return dept.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
        };
        const transformedData = data.map(employee => {
          const rawDept = employee.departmentName || employee.department?.name || employee.department || 'N/A';
          let positions = [];
          if (Array.isArray(employee.positions) && employee.positions.length > 0) {
            positions = employee.positions.map(p => ({
              positionName: p.positionName || '',
              fte: p.fte != null ? Number(p.fte) : 1.0
            }));
          } else {
            positions = [{ positionName: employee.position || '', fte: 1.0 }];
            if (employee.secondposition && employee.secondposition.trim() !== '') {
              positions.push({ positionName: employee.secondposition, fte: 0.5 });
            }
          }
          return {
            id: employee.employeeId || employee.id,
            firstName: employee.firstName,
            lastName: employee.lastName,
            name: `${employee.firstName} ${employee.lastName}`,
            email: employee.email || employee.user?.email || 'N/A',
            phone: employee.phone || 'N/A',
            department: formatDepartmentName(rawDept),
            departmentRaw: rawDept,
            office: employee.officeName || employee.office?.name || employee.office || 'N/A',
            positions,
            dateOfJoining: employee.dateOfJoining || employee.joiningDate || employee.startDate,
            contractType: getContractType(employee),
            employeeId: employee.employeeId || employee.id,
            dob: employee.dob || '',
            gender: employee.gender || '',
            cin: employee.cin || '',
            cnss: employee.cnss || '',
            diploma: employee.diploma || '',
            role: employee.user?.role || employee.role || 'EMPLOYEE',
            company: employee.company || 'N/A',
            projects: employee.projects || [],
            projectName: (employee.projects && employee.projects.length > 0) ? (employee.projects[0].projectName || employee.projects[0]) : (employee.projectName || 'N/A'),
            secondProjectName: (employee.projects && employee.projects.length > 1) ? (employee.projects[1].projectName || employee.projects[1]) : (employee.secondProjectName || 'N/A'),
            lastModifiedBy: employee.lastModifiedBy || employee.createdBy || 'SYSTEM',
            lastModifiedDate: employee.lastModifiedDate || employee.updatedAt || employee.createdDate || 'N/A',
            violationsCount: totalNegativeNotes[employee.employeeId || employee.id] || 0,
          };
        });
        setEmployees(transformedData);
        setFlaggedEmployees(flagged || {});
        setTotalEmployees(transformedData.length);
      } catch (err) {
        setError('Failed to load employees.');
        console.error('Fetch employees error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();

    const fetchProjects = async () => {
      try {
        const projects = await getAllProjects();
        setAvailableProjects(projects);
      } catch (err) {
        console.error('Failed to fetch projects:', err);
      }
    };
    fetchProjects();
  }, []);

  const getContractType = (employee) => {
    if (employee.contractType) return employee.contractType;
    if (employee.contract?.type) return employee.contract.type;
    if (employee.employmentType) return employee.employmentType;
    if (employee.type) return employee.type;
    if (employee.contractStatus) return employee.contractStatus;
    if (employee.employment && employee.employment.contractType) {
      return employee.employment.contractType;
    }
    if (employee.contract && employee.contract.contractType) {
      return employee.contract.contractType;
    }
    return '';
  };

  const formatDateDDMMYYYY = (date) => {
    if (!date) return '';
    let dateObj;
    if (typeof date === 'string') {
      dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) {
        dateObj = new Date(Date.parse(date));
      }
    } else {
      dateObj = new Date(date);
    }
    if (isNaN(dateObj.getTime())) return '';
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = dateObj.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const parseDateDDMMYYYYtoYYYYMMDD = (dateString) => {
    if (!dateString) return null;
    const parts = dateString.split('-');
    if (parts.length !== 3) return null;
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    const dateObj = new Date(year, month, day);
    if (isNaN(dateObj.getTime())) return null;
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const calculatePeriodWorked = (startDate) => {
    if (!startDate) return 'N/A';
    let start = new Date(startDate);
    if (isNaN(start.getTime())) start = new Date(Date.parse(startDate));
    if (isNaN(start.getTime())) return 'N/A';
    const now = new Date();
    const diffTime = Math.abs(now - start);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays < 31) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
    } else {
      const years = Math.floor(diffDays / 365);
      const months = Math.floor((diffDays % 365) / 30);
      let result = '';
      if (years > 0) result += `${years} year${years > 1 ? 's' : ''} `;
      if (months > 0) result += `${months} month${months > 1 ? 's' : ''}`;
      return result.trim();
    }
  };

  const getPositionsDisplay = (positions) => {
    if (!positions || positions.length === 0) return 'N/A';
    return positions
      .filter(p => p.positionName)
      .map(p => `${p.positionName} (${p.fte})`)
      .join('; ');
  };

  const getProjectsDisplay = (employee) => {
    if (employee.projects && employee.projects.length > 0) {
      return employee.projects.map(p => typeof p === 'string' ? p : p.projectName).filter(Boolean).join(' - ');
    }
    const projects = [];
    if (employee.projectName && employee.projectName.trim() !== '' && employee.projectName !== 'N/A') {
      projects.push(employee.projectName.trim());
    }
    if (employee.secondProjectName &&
      employee.secondProjectName.trim() !== '' &&
      employee.secondProjectName !== 'N/A') {
      projects.push(employee.secondProjectName.trim());
    }
    if (projects.length === 0) return 'No projects assigned';
    if (projects.length === 1) return projects[0];
    return projects.join(' - ');
  };

  const sortedEmployees = useMemo(() => {
    let sortableItems = [...employees];
    if (selectedViolations === 'Highest to Lowest') {
      sortableItems.sort((a, b) => (b.violationsCount || 0) - (a.violationsCount || 0));
    } else if (selectedViolations === 'Lowest to Highest') {
      sortableItems.sort((a, b) => (a.violationsCount || 0) - (b.violationsCount || 0));
    } else if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        if (sortConfig.key === 'dateOfJoining') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (sortConfig.key === 'age') {
          aValue = calculateAge(a.dob);
          bValue = calculateAge(b.dob);
          if (aValue === 'N/A') aValue = -1;
          if (bValue === 'N/A') bValue = -1;
        }
        if (typeof aValue === 'string') aValue = aValue.toLowerCase();
        if (typeof bValue === 'string') bValue = bValue.toLowerCase();
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [employees, sortConfig, selectedViolations]);

  const availableAges = useMemo(() => {
    const ages = new Set();
    employees.forEach((emp) => {
      const a = calculateAge(emp.dob);
      if (typeof a === 'number' && a > 0) ages.add(a);
    });
    return Array.from(ages).sort((a, b) => a - b);
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    return sortedEmployees.filter((emp) => {
      const matchesSearch = emp.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.phone.includes(searchTerm) ||
        String(calculateAge(emp.dob)).includes(searchTerm);
      const matchesDepartment = selectedDepartment === 'All' || emp.departmentRaw === selectedDepartment;
      const matchesOffice = selectedOffice === 'All' || emp.office === selectedOffice;
      const matchesCompany = selectedCompany === 'All' || emp.company === selectedCompany;
      const matchesGender = selectedGender === 'All' || emp.gender === selectedGender;
      const matchesProject = selectedProject === 'All' ||
        (emp.projects && emp.projects.some(p => {
          const name = typeof p === 'string' ? p : p.projectName;
          return name?.trim().toLowerCase() === selectedProject.toLowerCase();
        })) ||
        emp.projectName?.trim().toLowerCase() === selectedProject.toLowerCase() ||
        emp.secondProjectName?.trim().toLowerCase() === selectedProject.toLowerCase();
      const matchesJob = selectedJob === 'All' ||
        emp.positions.some(pos => pos.positionName === selectedJob);
      const matchesRole = selectedRole === 'All' || emp.role === selectedRole;
      const matchesViolations = selectedViolations === 'All' ||
        selectedViolations === 'Highest to Lowest' ||
        selectedViolations === 'Lowest to Highest' ||
        (selectedViolations === 'Has Violations' && (emp.violationsCount > 0)) ||
        (selectedViolations === 'No Violations' && (!emp.violationsCount || emp.violationsCount === 0));

      const age = calculateAge(emp.dob);
      const numAge = typeof age === 'number' ? age : (age !== 'N/A' && !isNaN(Number(age)) ? Number(age) : null);
      let matchesAge = true;
      if (selectedAgeFilter !== 'all') {
        if (selectedAgeFilter === 'not-specified') {
          matchesAge = numAge === null;
        } else if (numAge === null) {
          matchesAge = false;
        } else if (selectedAgeFilter === 'under-20') {
          matchesAge = numAge < 20;
        } else if (selectedAgeFilter === '20-25') {
          matchesAge = numAge >= 20 && numAge <= 25;
        } else if (selectedAgeFilter === '26-30') {
          matchesAge = numAge >= 26 && numAge <= 30;
        } else if (selectedAgeFilter === '31-35') {
          matchesAge = numAge >= 31 && numAge <= 35;
        } else if (selectedAgeFilter === '36-40') {
          matchesAge = numAge >= 36 && numAge <= 40;
        } else if (selectedAgeFilter === '41-50') {
          matchesAge = numAge >= 41 && numAge <= 50;
        } else if (selectedAgeFilter === 'above-50') {
          matchesAge = numAge > 50;
        } else if (selectedAgeFilter.startsWith('exact-')) {
          const targetAge = Number(selectedAgeFilter.replace('exact-', ''));
          matchesAge = numAge === targetAge;
        }
      }

      return matchesSearch && matchesDepartment && matchesOffice && matchesJob &&
        matchesCompany && matchesGender && matchesProject && matchesViolations && matchesRole && matchesAge;
    });
  }, [sortedEmployees, searchTerm, selectedDepartment, selectedOffice, selectedJob,
    selectedCompany, selectedGender, selectedProject, selectedViolations, selectedRole, selectedAgeFilter]);

  const averageAgeStats = useMemo(() => {
    const validAges = filteredEmployees
      .filter((emp) => String(emp.role || emp.user?.role || '').trim().toUpperCase() !== 'DIRECTOR')
      .map((emp) => calculateAge(emp.dob))
      .filter((age) => typeof age === 'number' && age > 0);
    if (validAges.length === 0) return { avg: 'N/A', count: 0 };
    const sum = validAges.reduce((acc, curr) => acc + curr, 0);
    return {
      avg: (sum / validAges.length).toFixed(1),
      count: validAges.length,
    };
  }, [filteredEmployees]);

  const ageDistributionChartData = useMemo(() => {
    const ageGroups = {
      'Under 20': 0,
      '20 - 25': 0,
      '26 - 30': 0,
      '31 - 35': 0,
      '36 - 40': 0,
      '41 - 50': 0,
      '50+': 0,
    };
    let notSpecified = 0;

    filteredEmployees.forEach((emp) => {
      const age = calculateAge(emp.dob);
      if (typeof age !== 'number' || isNaN(age) || age <= 0) {
        notSpecified++;
      } else if (age < 20) {
        ageGroups['Under 20']++;
      } else if (age <= 25) {
        ageGroups['20 - 25']++;
      } else if (age <= 30) {
        ageGroups['26 - 30']++;
      } else if (age <= 35) {
        ageGroups['31 - 35']++;
      } else if (age <= 40) {
        ageGroups['36 - 40']++;
      } else if (age <= 50) {
        ageGroups['41 - 50']++;
      } else {
        ageGroups['50+']++;
      }
    });

    const labels = Object.keys(ageGroups);
    const data = Object.values(ageGroups);
    if (notSpecified > 0) {
      labels.push('Not Specified');
      data.push(notSpecified);
    }

    const backgroundColors = [
      'rgba(99, 102, 241, 0.8)',
      'rgba(59, 130, 246, 0.8)',
      'rgba(14, 165, 233, 0.8)',
      'rgba(16, 185, 129, 0.8)',
      'rgba(245, 158, 11, 0.8)',
      'rgba(249, 115, 22, 0.8)',
      'rgba(239, 68, 68, 0.8)',
      'rgba(156, 163, 175, 0.8)',
    ].slice(0, labels.length);

    return {
      labels,
      datasets: [{
        label: 'Employees by Age',
        data,
        backgroundColor: backgroundColors,
        borderColor: backgroundColors.map((c) => c.replace('0.8', '1')),
        borderWidth: 1,
        hoverBackgroundColor: backgroundColors.map((c) => c.replace('0.8', '0.95')),
      }],
    };
  }, [filteredEmployees]);

  const paginatedEmployees = useMemo(() => {
    const startIndex = (currentPage - 1) * EMPLOYEES_PER_PAGE;
    return filteredEmployees.slice(startIndex, startIndex + EMPLOYEES_PER_PAGE);
  }, [filteredEmployees, currentPage]);

  const totalPages = useMemo(() => {
    return Math.ceil(filteredEmployees.length / EMPLOYEES_PER_PAGE);
  }, [filteredEmployees.length]);

  useEffect(() => {
    if (paginatedEmployees.length > 0) {
      const employeesNeedingImages = paginatedEmployees.filter(
        emp => emp.employeeId && !profileImages[emp.employeeId]
      );
      if (employeesNeedingImages.length > 0) {
        fetchEmployeeImages(employeesNeedingImages);
      }
    }
  }, [paginatedEmployees, profileImages, fetchEmployeeImages]);

  useEffect(() => {
    if (employees.length > 0 && Object.keys(profileImages).length === 0) {
      const firstPageEmployees = employees.slice(0, EMPLOYEES_PER_PAGE);
      fetchEmployeeImages(firstPageEmployees);
    }
  }, [employees, profileImages, fetchEmployeeImages]);

  const updateUrlParams = useCallback(() => {
    const params = {};
    if (searchTerm) params.q = searchTerm;
    if (selectedDepartment !== 'All') params.dept = selectedDepartment;
    if (selectedOffice !== 'All') params.office = selectedOffice;
    if (selectedJob !== 'All') params.job = selectedJob;
    if (selectedCompany !== 'All') params.company = selectedCompany;
    if (selectedGender !== 'All') params.gender = selectedGender;
    if (selectedRole !== 'All') params.role = selectedRole;
    if (selectedProject !== 'All') params.project = selectedProject;
    if (selectedViolations !== 'All') params.violations = selectedViolations;
    if (selectedAgeFilter !== 'all') params.age = selectedAgeFilter;
    if (currentPage !== 1) params.page = currentPage;
    setSearchParams(params, { replace: true });
  }, [
    searchTerm, selectedDepartment, selectedOffice, selectedJob,
    selectedCompany, selectedGender, selectedRole, selectedProject, selectedViolations, selectedAgeFilter, currentPage, setSearchParams
  ]);

  const handleAgeChange = (value) => {
    setSelectedAgeFilter(value);
    setCurrentPage(1);
  };

  useEffect(() => {
    updateUrlParams();
  }, [updateUrlParams]);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleDepartmentChange = (value) => {
    setSelectedDepartment(value);
    setCurrentPage(1);
  };

  const handleOfficeChange = (value) => {
    setSelectedOffice(value);
    setCurrentPage(1);
  };

  const handleJobChange = (value) => {
    setSelectedJob(value);
    setCurrentPage(1);
  };

  const handleRoleChange = (value) => {
    setSelectedRole(value);
    setCurrentPage(1);
  };

  const handleCompanyChange = (value) => {
    setSelectedCompany(value);
    setCurrentPage(1);
  };

  const handleGenderChange = (value) => {
    setSelectedGender(value);
    setCurrentPage(1);
  };

  const handleProjectChange = (value) => {
    setSelectedProject(value);
    setCurrentPage(1);
  };

  const handleViolationsChange = (value) => {
    setSelectedViolations(value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  const handleExportColumnToggle = (columnKey) => {
    setExportColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey] }));
  };

  const getExportColumnsAndData = () => {
    const columns = [];
    const dataKeys = [];
    Object.keys(exportColumns).forEach(key => {
      if (exportColumns[key]) {
        let header = key;
        switch (key) {
          case 'name': header = 'Full Name'; break;
          case 'email': header = 'Work Email'; break;
          case 'phone': header = 'Phone Number'; break;
          case 'age': header = 'Age'; break;
          case 'positions': header = 'Positions (FTE)'; break;
          case 'department': header = 'Department'; break;
          case 'office': header = 'Office'; break;
          case 'company': header = 'Company'; break;
          case 'contractType': header = 'Type of Contract'; break;
          case 'startDate': header = 'Start Date'; break;
          case 'periodWorked': header = 'Period Worked'; break;
          case 'projectName': header = 'Primary Project'; break;
          case 'secondProjectName': header = 'Secondary Project'; break;
          case 'gender': header = 'Gender'; break;
          default: header = key.charAt(0).toUpperCase() + key.slice(1);
        }
        columns.push({ header, dataKey: key });
        dataKeys.push(key);
      }
    });
    return { columns, dataKeys };
  };

  const exportToPDF = () => {
    if (!showExportOptions) return;
    try {
      const doc = new jsPDF();
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Team Members', doc.internal.pageSize.width / 2, 20, { align: 'center' });
      const { columns, dataKeys } = getExportColumnsAndData();
      const columnsForPDF = columns.map(col => col.header);
      const dataKeysForPDF = columns.map(col => col.dataKey);
      const tableData = filteredEmployees.map(emp => {
        const rowData = {};
        dataKeysForPDF.forEach(key => {
          switch (key) {
            case 'name':
              rowData[key] = `${emp.firstName} ${emp.lastName}`;
              break;
            case 'positions':
              rowData[key] = getPositionsDisplay(emp.positions);
              break;
            case 'office':
              rowData[key] = typeof emp.office === 'object' ? (emp.office?.name || 'N/A') : (emp.office || 'N/A');
              break;
            case 'startDate':
              rowData[key] = formatDateDDMMYYYY(emp.dateOfJoining);
              break;
            case 'periodWorked':
              rowData[key] = calculatePeriodWorked(emp.dateOfJoining);
              break;
            case 'projectName':
              rowData[key] = emp.projectName || 'N/A';
              break;
            case 'secondProjectName':
              rowData[key] = emp.secondProjectName || 'N/A';
              break;
            case 'age':
              rowData[key] = calculateAge(emp.dob);
              break;
            default:
              rowData[key] = emp[key] || 'N/A';
          }
        });
        return rowData;
      });
      const now = new Date();
      const exportDate = formatDateDDMMYYYY(now);
      const calculateColumnWidths = () => {
        const margin = 10;
        const availableWidth = doc.internal.pageSize.width - 2 * margin;
        const headerWidths = columnsForPDF.map(header => (header.length * 3.5));
        const dataWidths = dataKeysForPDF.map((key, index) => {
          const colData = tableData.map(row => row[key] ? row[key].toString() : '');
          const avgCharCount = colData.reduce((sum, val) => sum + val.length, 0) / colData.length;
          return avgCharCount * 3.5;
        });
        const combinedWidths = headerWidths.map((h, i) => Math.max(h, dataWidths[i], 10));
        const totalEstimatedWidth = combinedWidths.reduce((sum, w) => sum + w, 0);
        const adjustedWidths = combinedWidths.map(w => (w / totalEstimatedWidth) * availableWidth);
        return adjustedWidths.map((w, i) => ({ cellWidth: w }));
      };
      const columnStyles = calculateColumnWidths();
      autoTable(doc, {
        startY: 30,
        head: [columnsForPDF],
        body: tableData.map(row => dataKeysForPDF.map(key => row[key] || 'N/A')),
        theme: 'grid',
        headStyles: {
          fillColor: [59, 130, 246],
          fontSize: 7,
          fontStyle: 'bold',
          cellPadding: 1.5
        },
        styles: {
          font: 'helvetica',
          fontSize: 6.5,
          cellPadding: 1.2,
          minCellHeight: 4
        },
        columnStyles: Object.fromEntries(dataKeysForPDF.map((_, index) => [index, columnStyles[index]])),
        didDrawPage: (data) => {
          const pageSize = doc.internal.pageSize;
          const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
          const pageWidth = pageSize.width ? pageSize.width : pageSize.getWidth();
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100);
          doc.text(`AxoPeople • Exported on: ${exportDate}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
          const pageNumber = doc.getCurrentPageInfo().pageNumber;
          doc.text(`Page ${pageNumber}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
        }
      });
      doc.save('employees_export.pdf');
      setShowExportOptions(false);
    } catch (error) {
      console.error('PDF Export Error:', error);
      alert('Failed to generate PDF: ' + (error.message || 'Unknown error'));
    }
  };

  const exportToExcel = () => {
    if (!showExportOptions) return;
    try {
      const { columns, dataKeys } = getExportColumnsAndData();
      const headers = columns.map(col => col.header);
      const dataKeysForExcel = columns.map(col => col.dataKey);
      const worksheetData = filteredEmployees.map(emp => {
        const rowData = {};
        dataKeysForExcel.forEach(key => {
          switch (key) {
            case 'name':
              rowData[key] = `${emp.firstName} ${emp.lastName}`;
              break;
            case 'positions':
              rowData[key] = getPositionsDisplay(emp.positions);
              break;
            case 'office':
              rowData[key] = typeof emp.office === 'object' ? (emp.office?.name || 'N/A') : (emp.office || 'N/A');
              break;
            case 'startDate':
              rowData[key] = formatDateDDMMYYYY(emp.dateOfJoining);
              break;
            case 'periodWorked':
              rowData[key] = calculatePeriodWorked(emp.dateOfJoining);
              break;
            case 'projectName':
              rowData[key] = emp.projectName || 'N/A';
              break;
            case 'secondProjectName':
              rowData[key] = emp.secondProjectName || 'N/A';
              break;
            case 'age':
              rowData[key] = calculateAge(emp.dob);
              break;
            default:
              rowData[key] = emp[key] || 'N/A';
          }
        });
        return rowData;
      });
      const ws = XLSX.utils.json_to_sheet(worksheetData, { header: dataKeysForExcel });
      XLSX.utils.sheet_add_aoa(ws, [headers], { origin: "A1" });
      const colWidths = headers.map((header, index) => ({
        wch: Math.max(
          header.length,
          ...worksheetData.map(r => (r[dataKeysForExcel[index]]?.toString().length || 10))
        )
      }));
      ws['!cols'] = colWidths;
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Employees");
      XLSX.writeFile(wb, "employees_export.xlsx");
      setShowExportOptions(false);
    } catch (error) {
      console.error('Excel Export Error:', error);
      alert('Failed to generate Excel file: ' + (error.message || 'Unknown error'));
    }
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />;
    }
    return null;
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee.id);
    setEditFormData({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      email: employee.email || '',
      phone: employee.phone || '',
      department: employee.department || '',
      office: employee.office || '',
      startDate: formatDateDDMMYYYY(employee.dateOfJoining),
      contractType: employee.contractType || '',
      positions: employee.positions.map(p => ({
        positionName: p.positionName || '',
        fte: p.fte || 1.0
      })),
      company: employee.company || '',
      projectName: employee.projectName || 'N/A',
      secondProjectName: employee.secondProjectName || 'N/A',
      projects: employee.projects ? (Array.isArray(employee.projects) ? employee.projects.map(p => typeof p === 'string' ? p : p.projectName) : []) : []
    });
  };

  const handleSaveEmployee = async (employeeId) => {
    try {
      setSavingEmployee(employeeId);
      const dateObj = parseDateDDMMYYYYtoYYYYMMDD(editFormData.startDate);
      const validPositions = editFormData.positions.filter(p => p.positionName.trim() !== '');
      if (!isFTEValid(validPositions)) {
        setSuccessMessage('Total FTE must be between 0 and 1.0');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        return;
      }
      const allProjects = [editFormData.projectName, editFormData.secondProjectName]
        .filter(p => p && p !== 'N/A');

      const updateData = {
        firstName: editFormData.firstName,
        lastName: editFormData.lastName,
        email: editFormData.email,
        phone: editFormData.phone,
        office: editFormData.office,
        dateOfJoining: dateObj,
        departmentName: editFormData.department,
        contractType: editFormData.contractType,
        contractStartDate: dateObj,
        contractStatus: "ACTIVE",
        company: editFormData.company,
        projectName: editFormData.projectName || 'N/A',
        secondProjectName: editFormData.secondProjectName || 'N/A',
        projects: allProjects,
        positions: validPositions,
      };

      await updateEmployee(employeeId, updateData);

      const formatDept = (dept) => {
        if (!dept || dept === 'N/A') return dept;
        return dept.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
      };

      setEmployees(prev => prev.map(emp =>
        emp.id === employeeId
          ? {
            ...emp,
            ...updateData,
            name: `${editFormData.firstName} ${editFormData.lastName}`,
            department: formatDept(editFormData.department),
            departmentRaw: editFormData.department,
            dateOfJoining: dateObj
          }
          : emp
      ));
      setSuccessMessage('Employee updated successfully!');
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
      setEditingEmployee(null);
      setEditFormData({});
    } catch (error) {
      console.error('Error updating employee:', error);
      setSuccessMessage(`Error: ${error.response?.data?.message || error.message}`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } finally {
      setSavingEmployee(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingEmployee(null);
    setEditFormData({});
  };

  const handleShowFeedbackReport = (employee) => {
    setFeedbackReportEmployee(employee);
    setShowFeedbackReport(true);
  };

  const handleCloseFeedbackReport = () => {
    setShowFeedbackReport(false);
    setFeedbackReportEmployee(null);
  };

  const handleEditFieldChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditPositionChange = (index, field, value) => {
    setEditFormData(prev => {
      const newPositions = [...prev.positions];
      newPositions[index] = { ...newPositions[index], [field]: value };
      return { ...prev, positions: newPositions };
    });
  };

  const handleAddEditPosition = () => {
    setEditFormData(prev => ({
      ...prev,
      positions: [...prev.positions, { positionName: '', fte: 0.25 }]
    }));
  };

  const handleRemoveEditPosition = (index) => {
    setEditFormData(prev => {
      const newPositions = prev.positions.filter((_, i) => i !== index);
      return { ...prev, positions: newPositions };
    });
  };

  const handleDeleteEmployee = async (employeeId) => {
    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        await deleteEmployee(employeeId);
        setEmployees(prev => prev.filter(emp => emp.id !== employeeId));
        setProfileImages(prev => {
          const newImages = { ...prev };
          delete newImages[employeeId];
          return newImages;
        });
        setSuccessMessage('Employee deleted successfully!');
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        if (paginatedEmployees.length === 1 && currentPage > 1) {
          setCurrentPage(prev => prev - 1);
        }
      } catch (error) {
        setSuccessMessage(`Error deleting employee: ${error.message}`);
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
      }
    }
  };

  const handleImageClick = (employeeId) => {
    navigate(`/view-profile/${employeeId}`);
  };

  const resetCreateForm = useCallback(() => {
    setCreateFormData({
      firstName: '', lastName: '', email: '', password: '', role: '', departmentName: '',
      dateOfJoining: '', profileImage: null,
      positions: [{ positionName: '', fte: 1.0 }],
      dob: '', phone: '', office: '', gender: '', cin: '', diploma: '', cnss: '',
      company: '', projectName: '', secondProjectName: '',
    });
    setFormErrors({});
    setIsEditingInCreateForm(null);
    setNewImageUploaded(false);
    setShowMoreProjects(false);
    setSelectedAdditionalProjects([]);
  }, []);

  const validateCreateForm = useCallback(() => {
    const errors = {};
    const data = createFormData;
    if (!data.firstName.trim()) errors.firstName = 'First name is required';
    if (!data.lastName.trim()) errors.lastName = 'Last name is required';
    if (!data.email.trim()) errors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errors.email = 'Invalid email format';
    if (!isEditingInCreateForm && !data.password) errors.password = 'Password is required';
    if (isEditingInCreateForm && data.password && data.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }
    if (!data.role) errors.role = 'Role is required';
    if (!data.departmentName) errors.departmentName = 'Department is required';
    if (!data.dateOfJoining) errors.dateOfJoining = 'Joining date is required';
    const validPositions = data.positions.filter(p => p.positionName.trim() !== '');
    if (validPositions.length === 0) {
      errors.positions = 'At least one position is required';
    } else if (!isFTEValid(validPositions)) {
      errors.positions = 'Total FTE must be ≤ 1.0';
    }
    if (!data.dob) errors.dob = 'Date of birth is required';
    if (!data.phone) errors.phone = 'Phone number is required';
    if (!data.office) errors.office = 'Office is required';
    if (!data.gender) errors.gender = 'Gender is required';
    if (!data.cin) errors.cin = 'CIN is required';
    if (!data.diploma) errors.diploma = 'Diploma is required';
    if (!data.company) errors.company = 'Company is required';
    if (data.profileImage && newImageUploaded && data.profileImage.size > 3 * 1024 * 1024) {
      errors.profileImage = 'Image size must be less than 3MB';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [createFormData, isEditingInCreateForm, newImageUploaded]);

  const handleCreateFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateCreateForm()) return;
    try {
      let newEmployee;
      const validPositions = createFormData.positions.filter(p => p.positionName.trim() !== '');
      const formatDept = (dept) => {
        if (!dept || dept === 'N/A') return dept;
        return dept.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
      };

      if (isEditingInCreateForm) {
        const allProjects = [createFormData.projectName, createFormData.secondProjectName]
          .filter(p => p && p !== 'N/A');

        const updateData = {
          firstName: createFormData.firstName,
          lastName: createFormData.lastName,
          email: createFormData.email,
          positions: validPositions,
          dob: createFormData.dob,
          dateOfJoining: createFormData.dateOfJoining,
          phone: createFormData.phone,
          office: createFormData.office,
          gender: createFormData.gender,
          cin: createFormData.cin,
          diploma: createFormData.diploma,
          cnss: createFormData.cnss,
          departmentName: createFormData.departmentName,
          role: createFormData.role,
          contractType: "N/A",
          contractStartDate: createFormData.dateOfJoining,
          contractStatus: "ACTIVE",
          company: createFormData.company,
          projectName: createFormData.projectName || 'N/A',
          secondProjectName: createFormData.secondProjectName || 'N/A',
          projects: allProjects,
        };
        if (createFormData.password) {
          updateData.password = createFormData.password;
        }
        if (newImageUploaded && createFormData.profileImage) {
          updateData.image = createFormData.profileImage;
        }
        await updateEmployee(isEditingInCreateForm.id, updateData);

        setEmployees(prev => prev.map(emp =>
          emp.id === isEditingInCreateForm.id
            ? {
              ...emp,
              ...updateData,
              name: `${createFormData.firstName} ${createFormData.lastName}`,
              department: formatDept(createFormData.departmentName),
              departmentRaw: createFormData.departmentName
            }
            : emp
        ));
      } else {
        const employeeData = {
          email: createFormData.email,
          password: createFormData.password,
          firstName: createFormData.firstName,
          lastName: createFormData.lastName,
          positions: validPositions,
          dob: createFormData.dob,
          dateOfJoining: createFormData.dateOfJoining,
          phone: createFormData.phone,
          office: createFormData.office,
          gender: createFormData.gender,
          cin: createFormData.cin,
          diploma: createFormData.diploma,
          departmentName: createFormData.departmentName,
          role: createFormData.role,
          contractType: "N/A",
          contractStartDate: createFormData.dateOfJoining,
          contractStatus: "ACTIVE",
          contractFile: "default_contract.pdf",
          cnss: createFormData.cnss || "N/A",
          company: createFormData.company,
          projectName: createFormData.projectName || 'N/A',
          secondProjectName: createFormData.secondProjectName || 'N/A',
          projects: createFormData.projects || [],
        };
        if (createFormData.profileImage) {
          employeeData.image = createFormData.profileImage;
        }
        newEmployee = await registerEmployee(employeeData);
        const rawDept = newEmployee.departmentName || newEmployee.department?.name || (typeof newEmployee.department === 'string' ? newEmployee.department : '') || employeeData.departmentName || 'N/A';
        const officeVal = newEmployee.officeName || newEmployee.office?.name || (typeof newEmployee.office === 'string' ? newEmployee.office : '') || employeeData.office || 'N/A';
        const transformedEmployee = {
          id: newEmployee.employeeId || newEmployee.id || employeeData.employeeId,
          firstName: newEmployee.firstName || employeeData.firstName,
          lastName: newEmployee.lastName || employeeData.lastName,
          name: `${newEmployee.firstName || employeeData.firstName} ${newEmployee.lastName || employeeData.lastName}`,
          email: newEmployee.email || newEmployee.user?.email || employeeData.email || 'N/A',
          phone: newEmployee.phone || employeeData.phone || 'N/A',
          department: formatDept(rawDept),
          departmentRaw: rawDept,
          office: officeVal,
          positions: validPositions,
          dateOfJoining: newEmployee.dateOfJoining || newEmployee.joiningDate || newEmployee.startDate || employeeData.dateOfJoining,
          contractType: getContractType(newEmployee),
          employeeId: newEmployee.employeeId || newEmployee.id,
          dob: newEmployee.dob || employeeData.dob || '',
          gender: newEmployee.gender || employeeData.gender || '',
          cin: newEmployee.cin || employeeData.cin || '',
          cnss: newEmployee.cnss || employeeData.cnss || '',
          diploma: newEmployee.diploma || employeeData.diploma || '',
          role: newEmployee.user?.role || newEmployee.role || employeeData.role || 'EMPLOYEE',
          company: newEmployee.company || employeeData.company || 'N/A',
          projects: newEmployee.projects || employeeData.projects || [],
          projectName: newEmployee.projectName || newEmployee.ProjectName || (employeeData.projects && employeeData.projects.length > 0 ? employeeData.projects[0] : 'N/A'),
          secondProjectName: newEmployee.secondProjectName || newEmployee.SecondProjectName || (employeeData.projects && employeeData.projects.length > 1 ? employeeData.projects[1] : 'N/A'),
        };
        setEmployees(prev => [transformedEmployee, ...prev]);
        setTotalEmployees(prev => prev + 1);
      }
      setShowSuccess(true);
      setSuccessMessage(`Employee ${isEditingInCreateForm ? 'updated' : 'created'} successfully!`);
      setTimeout(() => setShowSuccess(false), 3000);
      setShowCreateForm(false);
      resetCreateForm();
    } catch (error) {
      console.error('Submission error:', error);
      setSuccessMessage(`Failed to ${isEditingInCreateForm ? 'update' : 'create'} employee: ${error.response?.data?.error || error.message}`);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }
  };

  const handleCreateFormInputChange = (field, value) => {
    setCreateFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleCreatePositionChange = (index, field, value) => {
    setCreateFormData(prev => {
      const newPositions = [...prev.positions];
      newPositions[index] = { ...newPositions[index], [field]: value };
      return { ...prev, positions: newPositions };
    });
  };

  const handleAddCreatePosition = () => {
    if (createFormData.positions.length >= 4) return;
    setCreateFormData(prev => ({
      ...prev,
      positions: [...prev.positions, { positionName: '', fte: 0.25 }]
    }));
  };

  const handleRemoveCreatePosition = (index) => {
    setCreateFormData(prev => {
      const newPositions = prev.positions.filter((_, i) => i !== index);
      return { ...prev, positions: newPositions };
    });
  };

  const handleCreateFormImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 3 * 1024 * 1024) {
        setFormErrors(prev => ({ ...prev, profileImage: 'Image size must be less than 3MB' }));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.replace(/^image\/[a-z]+;base64,/, '');
        setCreateFormData(prev => ({ ...prev, profileImage: base64String }));
        setNewImageUploaded(true);
        setFormErrors(prev => {
          const { profileImage, ...rest } = prev;
          return rest;
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEditInModal = (employee) => {
    setCreateFormData({
      firstName: employee.firstName || '',
      lastName: employee.lastName || '',
      email: employee.email || '',
      password: '',
      role: employee.role || 'EMPLOYEE',
      departmentName: employee.department || '',
      dateOfJoining: employee.dateOfJoining || '',
      profileImage: employee.profileImage ? `${IMAGE_BASE_URL}${employee.employeeId}` : null,
      positions: employee.positions.map(p => ({
        positionName: p.positionName || '',
        fte: p.fte || 1.0
      })),
      dob: employee.dob || '',
      phone: employee.phone || '',
      office: employee.office || '',
      gender: employee.gender || '',
      cin: employee.cin || '',
      cnss: employee.cnss || '',
      diploma: employee.diploma || '',
      company: employee.company || '',
      projectName: employee.projectName || 'N/A',
      secondProjectName: employee.secondProjectName || 'N/A',
      projects: employee.projects ? (Array.isArray(employee.projects) ? employee.projects.map(p => typeof p === 'string' ? p : p.projectName) : []) : []
    });
    setIsEditingInCreateForm(employee);
    setNewImageUploaded(false);
    setFormErrors({});
    setShowCreateForm(true);
  };

  const handleCloseCreateForm = () => {
    setShowCreateForm(false);
    resetCreateForm();
  };

  if (loading && employees.length === 0) return (
    <div className="employees__container">
      <div className="employees__table-loading">Loading employees...</div>
    </div>
  );

  if (error) return (
    <div className="employees__container">
      <div className="error-message">{error}</div>
    </div>
  );

  const renderHeaderCell = (label, sortKey) => (
    <th
      className="employees__table-header"
      onClick={() => handleSort(sortKey)}
      style={{ cursor: 'pointer', userSelect: 'none' }}
    >
      {label} {getSortIcon(sortKey)}
    </th>
  );

  return (
    <div className="employees__container">
      <PageHeader
        title="Employees Management"
        subtitle="Track and manage your company's workforce"
        actions={
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
            <button
              onClick={() => {
                setShowCreateForm(true);
                setIsEditingInCreateForm(null);
              }}
              className="animated-card"
              style={{
                padding: '0.6rem 1.1rem',
                background: `linear-gradient(135deg, ${THEME_PRIMARY}, ${THEME_SECONDARY})`,
                color: 'white',
                border: 'none',
                borderRadius: '0.6rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem',
                boxShadow: `0 6px 18px ${THEME_PRIMARY}25`
              }}
            >
              <Plus size={16} /> Create Employee
            </button>
            <button
              onClick={() => setShowExportOptions(true)}
              className="animated-card"
              style={{
                padding: '0.6rem 1.1rem',
                background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                color: '#475569',
                border: '1px solid #cbd5e1',
                borderRadius: '0.6rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem'
              }}
            >
              <Settings size={16} /> Export Options
            </button>
            <button
              onClick={() => setShowFullFeedbackReport(true)}
              title="View All Employees Feedback Report"
              className="animated-card"
              style={{
                padding: '0.6rem 1.1rem',
                background: 'linear-gradient(135deg, #FEE2E2, #FECACA)',
                color: '#EF4444',
                border: 'none',
                borderRadius: '0.6rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem'
              }}
            >
              <FileText size={16} /> Feedback Report
            </button>
            <button
              onClick={() => setShowAuditInfo(!showAuditInfo)}
              className="animated-card"
              style={{
                padding: '0.6rem 1.1rem',
                background: showAuditInfo ? 'linear-gradient(135deg, #475569, #1e293b)' : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                color: showAuditInfo ? 'white' : '#475569',
                border: '1px solid #cbd5e1',
                borderRadius: '0.6rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.45rem'
              }}
            >
              <Clock size={16} /> {showAuditInfo ? 'Hide' : 'Show'} Audit
            </button>
          </div>
        }
      />
      <section className="employees__table-section">
        <div className="employees__table-card">
          <div className="employees__table-header-container" style={{ padding: '0.1rem 0.1rem' }}>
            <div className="employees__filter-container" style={{ gap: '0.5rem', padding: '0.5rem 0', margin: '0rem 0' }}>
              <div className="employees__filter-group">
                <label htmlFor="office-filter" className="employees__filter-label">Office Location:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <MapPin
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      color: '#2563EB',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    id="office-filter"
                    value={selectedOffice}
                    onChange={(e) => handleOfficeChange(e.target.value)}
                    className="employees__filter-select"
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.35rem 2.2rem 0.35rem 2.1rem',
                      height: 'auto',
                      borderColor: selectedOffice && selectedOffice !== 'All' ? '#2563EB' : undefined,
                      fontWeight: selectedOffice && selectedOffice !== 'All' ? 600 : undefined
                    }}
                  >
                    {offices.map(office => (
                      <option key={office} value={office}>{office}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="employees__filter-group">
                <label htmlFor="department-filter" className="employees__filter-label">Department:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Users
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      color: '#2563EB',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    id="department-filter"
                    value={selectedDepartment}
                    onChange={(e) => handleDepartmentChange(e.target.value)}
                    className="employees__filter-select"
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.35rem 2.2rem 0.35rem 2.1rem',
                      height: 'auto',
                      borderColor: selectedDepartment && selectedDepartment !== 'All' ? '#2563EB' : undefined,
                      fontWeight: selectedDepartment && selectedDepartment !== 'All' ? 600 : undefined
                    }}
                  >
                    {departments.map(dept => (
                      <option key={dept} value={dept}>
                        {dept === 'Customer_services' ? 'Customer Services' : dept}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="employees__filter-group">
                <label htmlFor="company-filter" className="employees__filter-label">Company:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Building2
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      color: '#2563EB',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    id="company-filter"
                    value={selectedCompany}
                    onChange={(e) => handleCompanyChange(e.target.value)}
                    className="employees__filter-select"
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.35rem 2.2rem 0.35rem 2.1rem',
                      height: 'auto',
                      borderColor: selectedCompany && selectedCompany !== 'All' ? '#2563EB' : undefined,
                      fontWeight: selectedCompany && selectedCompany !== 'All' ? 600 : undefined
                    }}
                  >
                    {companyFilterOptions.map(company => (
                      <option key={company} value={company}>{company}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="employees__filter-group">
                <label htmlFor="project-filter" className="employees__filter-label">Project:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <FolderOpen
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      color: '#2563EB',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    id="project-filter"
                    value={selectedProject}
                    onChange={(e) => handleProjectChange(e.target.value)}
                    className="employees__filter-select"
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.35rem 2.2rem 0.35rem 2.1rem',
                      height: 'auto',
                      borderColor: selectedProject && selectedProject !== 'All' ? '#2563EB' : undefined,
                      fontWeight: selectedProject && selectedProject !== 'All' ? 600 : undefined
                    }}
                  >
                    {projectFilterOptions.map(project => (
                      <option key={project} value={project}>{project}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="employees__filter-group">
                <label htmlFor="gender-filter" className="employees__filter-label">Gender:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <User
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      color: '#2563EB',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    id="gender-filter"
                    value={selectedGender}
                    onChange={(e) => handleGenderChange(e.target.value)}
                    className="employees__filter-select"
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.35rem 2.2rem 0.35rem 2.1rem',
                      height: 'auto',
                      borderColor: selectedGender && selectedGender !== 'All' ? '#2563EB' : undefined,
                      fontWeight: selectedGender && selectedGender !== 'All' ? 600 : undefined
                    }}
                  >
                    {genders.map(gender => (
                      <option key={gender} value={gender}>{gender}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="employees__filter-group">
                <label htmlFor="violations-filter" className="employees__filter-label">Violations:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <AlertTriangle
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      color: '#2563EB',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    id="violations-filter"
                    value={selectedViolations}
                    onChange={(e) => handleViolationsChange(e.target.value)}
                    className="employees__filter-select"
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.35rem 2.2rem 0.35rem 2.1rem',
                      height: 'auto',
                      borderColor: selectedViolations && selectedViolations !== 'All' ? '#2563EB' : undefined,
                      fontWeight: selectedViolations && selectedViolations !== 'All' ? 600 : undefined
                    }}
                  >
                    <option value="All">All Violations</option>
                    <option value="Highest to Lowest">Highest to Lowest</option>
                    <option value="Lowest to Highest">Lowest to Highest</option>
                    <option value="Has Violations">Has Violations</option>
                    <option value="No Violations">No Violations</option>
                  </select>
                </div>
              </div>
              <div className="employees__filter-group">
                <label htmlFor="job-filter" className="employees__filter-label">Job Title:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Briefcase
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      color: '#2563EB',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    id="job-filter"
                    value={selectedJob}
                    onChange={(e) => handleJobChange(e.target.value)}
                    className="employees__filter-select"
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.35rem 2.2rem 0.35rem 2.1rem',
                      height: 'auto',
                      borderColor: selectedJob && selectedJob !== 'All' ? '#2563EB' : undefined,
                      fontWeight: selectedJob && selectedJob !== 'All' ? 600 : undefined
                    }}
                  >
                    {jobTitles.map(job => (
                      <option key={job} value={job}>{job}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="employees__filter-group">
                <label htmlFor="role-filter" className="employees__filter-label">Role:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Shield
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      color: '#2563EB',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    id="role-filter"
                    value={selectedRole}
                    onChange={(e) => handleRoleChange(e.target.value)}
                    className="employees__filter-select"
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.35rem 2.2rem 0.35rem 2.1rem',
                      height: 'auto',
                      borderColor: selectedRole && selectedRole !== 'All' ? '#2563EB' : undefined,
                      fontWeight: selectedRole && selectedRole !== 'All' ? 600 : undefined
                    }}
                  >
                    {roleFilterOptions.map(role => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="employees__search-container">
                <label htmlFor="search-input" className="employees__search-label">Search:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Search
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      color: '#2563EB',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <input
                    id="search-input"
                    type="text"
                    placeholder="Search employees..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="employees__search-input"
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.35rem 0.75rem 0.35rem 2.1rem',
                      height: 'auto',
                      borderColor: searchTerm ? '#2563EB' : undefined,
                      fontWeight: searchTerm ? 600 : undefined
                    }}
                  />
                </div>
              </div>
              <div className="employees__filter-group">
                <label htmlFor="age-filter" className="employees__filter-label">Age:</label>
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <Calendar
                    size={16}
                    style={{
                      position: 'absolute',
                      left: '10px',
                      color: '#2563EB',
                      pointerEvents: 'none',
                      zIndex: 1,
                      flexShrink: 0
                    }}
                  />
                  <select
                    id="age-filter"
                    value={selectedAgeFilter}
                    onChange={(e) => handleAgeChange(e.target.value)}
                    className="employees__filter-select"
                    style={{
                      fontSize: '0.875rem',
                      padding: '0.35rem 2.2rem 0.35rem 2.1rem',
                      height: 'auto',
                      borderColor: selectedAgeFilter && selectedAgeFilter !== 'all' ? '#2563EB' : undefined,
                      fontWeight: selectedAgeFilter && selectedAgeFilter !== 'all' ? 600 : undefined
                    }}
                  >
                    <option value="all">All Ages</option>
                    <optgroup label="Age Ranges">
                      <option value="under-20">Under 20</option>
                      <option value="20-25">20 - 25</option>
                      <option value="26-30">26 - 30</option>
                      <option value="31-35">31 - 35</option>
                      <option value="36-40">36 - 40</option>
                      <option value="41-50">41 - 50</option>
                      <option value="above-50">50+</option>
                      <option value="not-specified">Not Specified</option>
                    </optgroup>
                    {availableAges.length > 0 && (
                      <optgroup label="Exact Age">
                        {availableAges.map((age) => (
                          <option key={age} value={`exact-${age}`}>{age} years old</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
              </div>
              <div
                className="employees__avg-age-badge"
                title={`Average age of ${averageAgeStats.count} employees with recorded DOB (Click to view Age Distribution Chart)`}
                style={{
                  marginTop: '32px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.45rem',
                  padding: '0.32rem 0.75rem',
                  backgroundColor: '#EFF6FF',
                  border: '1px solid #BFDBFE',
                  borderRadius: '0.5rem',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  color: '#1E40AF',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  boxShadow: '0 1px 3px rgba(30, 64, 175, 0.08)'
                }}
                onClick={() => setShowAgeDistributionModal(true)}
              >
                <Clock size={16} color="#2563EB" />
                <span>Avg Age Total:</span>
                <span style={{
                  backgroundColor: '#2563EB',
                  color: '#FFFFFF',
                  padding: '0.12rem 0.5rem',
                  borderRadius: '0.375rem',
                  fontWeight: '700',
                  fontSize: '0.825rem'
                }}>
                  {averageAgeStats.avg !== 'N/A' ? `${averageAgeStats.avg} yrs` : 'N/A'}
                </span>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '2px 4px',
                    borderRadius: '4px',
                    backgroundColor: '#DBEAFE',
                    color: '#1D4ED8',
                    marginLeft: '2px'
                  }}
                  title="View Age Distribution Chart"
                >
                  <BarChart3 size={14} />
                </span>
              </div>
            </div>
          </div>
          <div className="employees__tabs">
            <button className={`employees__tab-btn ${activeTab === 'personal' ? 'active' : ''}`} onClick={() => setActiveTab('personal')}>
              Personal Information
            </button>
            <button className={`employees__tab-btn ${activeTab === 'job' ? 'active' : ''}`} onClick={() => setActiveTab('job')}>
              Job Details
            </button>
            <button className={`employees__tab-btn ${activeTab === 'employment' ? 'active' : ''}`} onClick={() => setActiveTab('employment')}>
              Employment Information
            </button>
            <button className={`employees__tab-btn ${activeTab === 'academic' ? 'active' : ''}`} onClick={() => setActiveTab('academic')}>
              Academic Background
            </button>
            <button className={`employees__tab-btn ${activeTab === 'audit' ? 'active' : ''}`} onClick={() => setActiveTab('audit')}>
              Audit
            </button>
          </div>
          <div className="employees__table-wrapper">
            <table className="employees__table">
              <thead>
                <tr className="employees__table-header-row">
                  <th className="employees__table-header employees__fixed-col">Image</th>
                  {renderHeaderCell('Full Name', 'name')}
                  {activeTab === 'personal' && (
                    <>
                      {renderHeaderCell('Work Email', 'email')}
                      {renderHeaderCell('Phone Number', 'phone')}
                      {renderHeaderCell('Age', 'age')}
                    </>
                  )}
                  {activeTab === 'job' && (
                    <>
                      {renderHeaderCell('Positions (FTE)', 'positions')}
                      {renderHeaderCell('Projects', 'projectName')}
                      {renderHeaderCell('Department', 'department')}
                      {renderHeaderCell('Office Location', 'office')}
                      {renderHeaderCell('Company', 'company')}
                    </>
                  )}
                  {activeTab === 'employment' && (
                    <>
                      {renderHeaderCell('Start Date', 'dateOfJoining')}
                      {renderHeaderCell('Type of Contract', 'contractType')}
                      {renderHeaderCell('Period Worked', 'dateOfJoining')}
                    </>
                  )}
                  {activeTab === 'academic' && (
                    <>
                      {renderHeaderCell('Diploma', 'diploma')}
                    </>
                  )}
                  {activeTab === 'audit' && (
                    <>
                      {renderHeaderCell('Last Modified Date', 'lastModifiedDate')}
                      {renderHeaderCell('Last Modified By', 'lastModifiedBy')}
                    </>
                  )}
                  {showAuditInfo && activeTab !== 'audit' && (
                    <>
                      {renderHeaderCell('Modified Date', 'lastModifiedDate')}
                      {renderHeaderCell('Modified By', 'lastModifiedBy')}
                    </>
                  )}
                  <th className="employees__table-header employees__fixed-col employees__actions-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedEmployees.map((emp, index) => {
                  if (editingEmployee === emp.id) {
                    return (
                      <tr
                        key={emp.id}
                        className="employees__table-row editing"
                        style={{ animationDelay: `${index * 100}ms` }}
                      >
                        <td className="employees__table-cell employees__image-cell">
                          <div className="employees__avatar">
                            {profileImages[emp.employeeId] ? (
                              <img
                                src={profileImages[emp.employeeId]}
                                alt={`${emp.firstName} ${emp.lastName}'s profile`}
                                className="employees__avatar-image"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : (
                              <span className="employees__avatar-placeholder">
                                {emp.firstName?.charAt(0)}{emp.lastName?.charAt(0)}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="employees__table-cell">
                          <div>
                            <input
                              type="text"
                              value={editFormData.firstName}
                              onChange={(e) => handleEditFieldChange('firstName', e.target.value)}
                              className="employees__edit-input"
                              placeholder="First Name"
                            />
                            <input
                              type="text"
                              value={editFormData.lastName}
                              onChange={(e) => handleEditFieldChange('lastName', e.target.value)}
                              className="employees__edit-input"
                              placeholder="Last Name"
                              style={{ marginTop: '4px' }}
                            />
                          </div>
                        </td>
                        {activeTab === 'personal' && (
                          <>
                            <td className="employees__table-cell">
                              <input
                                type="email"
                                value={editFormData.email}
                                onChange={(e) => handleEditFieldChange('email', e.target.value)}
                                className="employees__edit-input"
                                placeholder="Email"
                              />
                            </td>
                            <td className="employees__table-cell">
                              <input
                                type="tel"
                                value={editFormData.phone}
                                onChange={(e) => handleEditFieldChange('phone', e.target.value)}
                                className="employees__edit-input"
                                placeholder="Phone"
                              />
                            </td>
                            <td className="employees__table-cell">
                              <span style={{ fontSize: '0.875rem', color: '#64748B' }}>
                                {calculateAge(editFormData.dob || emp.dob)}
                              </span>
                            </td>
                          </>
                        )}
                        {activeTab === 'job' && (
                          <>
                            <td className="employees__table-cell">
                              <div className="employees__positions-edit">
                                {editFormData.positions.map((pos, idx) => (
                                  <div key={idx} className="employees__position-edit-row" style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                    <select
                                      value={pos.positionName}
                                      onChange={(e) => handleEditPositionChange(idx, 'positionName', e.target.value)}
                                      className="employees__edit-select"
                                      style={{ marginRight: '8px', flex: 1 }}
                                    >
                                      <option value="">Select Position</option>
                                      {POSITION_OPTIONS.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                      ))}
                                    </select>
                                    <select
                                      value={pos.fte}
                                      onChange={(e) => handleEditPositionChange(idx, 'fte', Number(e.target.value))}
                                      className="employees__edit-select"
                                      style={{ width: '80px', marginRight: '8px' }}
                                    >
                                      {FTE_OPTIONS.map(fte => (
                                        <option key={fte} value={fte}>{fte}</option>
                                      ))}
                                    </select>
                                    {editFormData.positions.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveEditPosition(idx)}
                                        className="employees__action-icon-btn employees__delete-icon-btn"
                                        style={{ padding: '2px', minWidth: '24px' }}
                                      >
                                        <Minus size={12} />
                                      </button>
                                    )}
                                  </div>
                                ))}
                                {editFormData.positions.length < 4 && (
                                  <button
                                    type="button"
                                    onClick={handleAddEditPosition}
                                    disabled={editFormData.positions.length >= 4}
                                    style={{
                                      background: 'linear-gradient(135deg, #1f2937 0%, #3b82f6 100%)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      padding: '4px 8px',
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      cursor: editFormData.positions.length >= 4 ? 'not-allowed' : 'pointer',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      marginTop: '6px',
                                    }}
                                  >
                                    <Plus size={12} />
                                    Add
                                  </button>
                                )}
                                {createFormData.positions.length >= 4 && (
                                  <div style={{
                                    marginTop: '8px',
                                    fontSize: '0.875rem',
                                    color: '#666',
                                    fontStyle: 'italic'
                                  }}>
                                    ⚠️ Max 4 positions allowed
                                  </div>
                                )}
                                <div style={{ marginTop: '4px', fontSize: '0.875rem' }}>
                                  Total FTE: {calculateTotalFTE(editFormData.positions).toFixed(2)}
                                  {!isFTEValid(editFormData.positions) && (
                                    <span style={{ color: 'red', marginLeft: '8px' }}>⚠️ Invalid</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="employees__table-cell">
                              <div className="employees__projects-container">
                                <div className="employees__project-input-group">
                                  <label>Primary Project</label>
                                  <select
                                    value={editFormData.projectName || 'N/A'}
                                    onChange={(e) => handleEditFieldChange('projectName', e.target.value)}
                                    className="employees__edit-select"
                                  >
                                    {projectOptions.map(project => (
                                      <option key={project} value={project}>{project}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="employees__project-input-group">
                                  <label>Secondary Project (Optional)</label>
                                  <select
                                    value={editFormData.secondProjectName || 'N/A'}
                                    onChange={(e) => handleEditFieldChange('secondProjectName', e.target.value)}
                                    className="employees__edit-select"
                                  >
                                    {projectOptions.map(project => (
                                      <option key={project} value={project}>{project}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                            </td>
                            <td className="employees__table-cell">
                              <select
                                value={editFormData.department}
                                onChange={(e) => handleEditFieldChange('department', e.target.value)}
                                className="employees__edit-select"
                              >
                                <option value="">Select Department</option>
                                {departmentOptions
                                  .filter(d => d.value !== 'All')
                                  .map(dept => (
                                    <option key={dept.value} value={dept.value}>{dept.label}</option>
                                  ))}
                              </select>
                            </td>
                            <td className="employees__table-cell">
                              <select
                                value={editFormData.office}
                                onChange={(e) => handleEditFieldChange('office', e.target.value)}
                                className="employees__edit-select"
                              >
                                <option value="">Select Office</option>
                                {offices.filter(o => o !== 'All').map(office => (
                                  <option key={office} value={office}>{office}</option>
                                ))}
                              </select>
                            </td>
                            <td className="employees__table-cell">
                              <select
                                value={editFormData.company}
                                onChange={(e) => handleEditFieldChange('company', e.target.value)}
                                className="employees__edit-select"
                              >
                                <option value="">Select Company</option>
                                {companyOptions.map(comp => (
                                  <option key={comp} value={comp}>{comp}</option>
                                ))}
                              </select>
                            </td>
                          </>
                        )}
                        {activeTab === 'employment' && (
                          <>
                            <td className="employees__table-cell">
                              <input
                                type="text"
                                value={editFormData.startDate}
                                onChange={(e) => handleEditFieldChange('startDate', e.target.value)}
                                className="employees__edit-input"
                                placeholder="DD-MM-YYYY"
                                pattern="\d{2}-\d{2}-\d{4}"
                              />
                            </td>
                            <td className="employees__table-cell">
                              <select
                                value={editFormData.contractType}
                                onChange={(e) => handleEditFieldChange('contractType', e.target.value)}
                                className="employees__edit-select"
                              >
                                {contractTypes.map(type => (
                                  <option key={type} value={type}>{type}</option>
                                ))}
                              </select>
                            </td>
                            <td className="employees__table-cell">
                              {calculatePeriodWorked(parseDateDDMMYYYYtoYYYYMMDD(editFormData.startDate))}
                            </td>
                          </>
                        )}
                        <td className="employees__table-cell employees__actions-cell">
                          <div className="employees__action-buttons">
                            <button
                              className="employees__action-icon-btn employees__save-icon-btn"
                              onClick={() => handleSaveEmployee(emp.id)}
                              disabled={savingEmployee === emp.id}
                              title="Save"
                            >
                              <Save size={16} />
                            </button>
                            <button
                              className="employees__action-icon-btn employees__cancel-icon-btn"
                              onClick={handleCancelEdit}
                              disabled={savingEmployee === emp.id}
                              title="Cancel"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr
                      key={emp.id}
                      className="employees__table-row"
                      style={{
                        animationDelay: `${index * 100}ms`,
                        backgroundColor: emp.violationsCount >= 3 ? '#FFFBEB' : undefined,
                        borderLeft: emp.violationsCount >= 3 ? '4px solid #F59E0B' : undefined,
                      }}
                    >
                      <td className="employees__table-cell employees__image-cell">
                        <div
                          className="employees__avatar"
                          onClick={() => handleImageClick(emp.id)}
                          style={{ cursor: 'pointer', position: 'relative' }}
                        >
                          {profileImages[emp.employeeId] ? (
                            <img
                              src={profileImages[emp.employeeId]}
                              alt={`${emp.firstName} ${emp.lastName}'s profile`}
                              className="employees__avatar-image"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : (
                            <span className="employees__avatar-placeholder">
                              {emp.firstName?.charAt(0)}{emp.lastName?.charAt(0)}
                            </span>
                          )}
                          {emp.violationsCount > 0 ? (
                            <span
                              style={{
                                position: 'absolute',
                                top: '2px',
                                right: '2px',
                                backgroundColor: emp.violationsCount >= 3 ? '#F59E0B' : '#9CA3AF',
                                color: 'white',
                                borderRadius: '9999px',
                                minWidth: '18px',
                                height: '18px',
                                padding: '0 4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.7rem',
                                fontWeight: '700',
                                lineHeight: '1',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                                border: '1px solid #FFFFFF'
                              }}
                              title={`Violations: ${flaggedEmployees[emp.employeeId]?.join(', ') || 'Various'}`}
                            >
                              {emp.violationsCount}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="employees__table-cell">
                        <div className="employees__employee-name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          {emp.violationsCount >= 3 ? (
                            <AlertTriangle size={16} color="#F59E0B" title="Repeated feedback" />
                          ) : null}
                          {emp.firstName} {emp.lastName}
                        </div>
                      </td>
                      {activeTab === 'personal' && (
                        <>
                          <td className="employees__table-cell">{emp.email}</td>
                          <td className="employees__table-cell">{emp.phone}</td>
                          <td className="employees__table-cell">{calculateAge(emp.dob)}</td>
                        </>
                      )}
                      {activeTab === 'job' && (
                        <>
                          <td className="employees__table-cell">
                            {getPositionsDisplay(emp.positions)}
                          </td>
                          <td className="employees__table-cell">
                            <div className="employees__projects-display">
                              {getProjectsDisplay(emp)}
                            </div>
                          </td>
                          <td className="employees__table-cell">
                            <span className={`employees__department-badge employees__department-badge--${emp.department?.toLowerCase().replace(/\s+/g, '_') || 'default'}`}>
                              {emp.department}
                            </span>
                          </td>
                          <td className="employees__table-cell">
                            {typeof emp.office === 'object' ? (emp.office?.name || 'N/A') : (emp.office || 'N/A')}
                          </td>
                          <td className="employees__table-cell">{emp.company}</td>
                        </>
                      )}
                      {activeTab === 'employment' && (
                        <>
                          <td className="employees__table-cell">
                            {formatDateDDMMYYYY(emp.dateOfJoining)}
                          </td>
                          <td className="employees__table-cell">
                            {emp.contractType}
                          </td>
                          <td className="employees__table-cell">
                            {calculatePeriodWorked(emp.dateOfJoining)}
                          </td>
                        </>
                      )}
                      {activeTab === 'academic' && (
                        <>
                          <td className="employees__table-cell">{emp.diploma || 'N/A'}</td>
                        </>
                      )}
                      {activeTab === 'audit' && (
                        <>
                          <td className="employees__table-cell">
                            {emp.lastModifiedDate && emp.lastModifiedDate !== 'N/A'
                              ? format(new Date(emp.lastModifiedDate), 'dd-MM-yyyy HH:mm')
                              : 'N/A'}
                          </td>
                          <td className="employees__table-cell">
                            <span className="employees__department-badge" style={{ backgroundColor: '#F3F4F6', color: '#374151' }}>
                              {emp.lastModifiedBy}
                            </span>
                          </td>
                        </>
                      )}
                      {showAuditInfo && activeTab !== 'audit' && (
                        <>
                          <td className="employees__table-cell" style={{ fontSize: '0.75rem' }}>
                            {emp.lastModifiedDate && emp.lastModifiedDate !== 'N/A'
                              ? format(new Date(emp.lastModifiedDate), 'dd-MM HH:mm')
                              : 'N/A'}
                          </td>
                          <td className="employees__table-cell">
                            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              {emp.lastModifiedBy?.split(' ')[0]}
                            </span>
                          </td>
                        </>
                      )}
                      <td className="employees__table-cell employees__actions-cell">
                        <div className="employees__action-buttons">
                          <button
                            className="employees__action-icon-btn employees__edit-icon-btn"
                            onClick={() => handleEditEmployee(emp)}
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className="employees__action-icon-btn employees__details-icon-btn"
                            onClick={() => handleEditInModal(emp)}
                            title="Details"
                          >
                            <User size={16} />
                          </button>
                          <button
                            className="employees__action-icon-btn employees__report-icon-btn"
                            onClick={() => handleShowFeedbackReport(emp)}
                            title="Feedback Report"
                            style={{ backgroundColor: '#EFF6FF', color: '#3B82F6' }}
                          >
                            <BarChart3 size={16} />
                          </button>
                          <button
                            className="employees__action-icon-btn employees__delete-icon-btn"
                            onClick={() => handleDeleteEmployee(emp.id)}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {totalPages > 1 && (
              <div className="employees__pagination">
                <div className="employees__pagination-info">
                  Showing {(currentPage - 1) * EMPLOYEES_PER_PAGE + 1} to {Math.min(currentPage * EMPLOYEES_PER_PAGE, filteredEmployees.length)} of {filteredEmployees.length} employees
                </div>
                <div className="employees__pagination-controls">
                  <button
                    className="employees__pagination-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} /> Previous
                  </button>
                  <div className="employees__pagination-pages">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let page;
                      if (totalPages <= 5) {
                        page = i + 1;
                      } else {
                        if (currentPage <= 3) {
                          page = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          page = totalPages - 4 + i;
                        } else {
                          page = currentPage - 2 + i;
                        }
                      }
                      return (
                        <button
                          key={page}
                          className={`employees__pagination-page-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      );
                    })}
                    {totalPages > 5 && (
                      <>
                        {currentPage < totalPages - 2 && <span>...</span>}
                        {currentPage < totalPages - 1 && (
                          <button
                            className="employees__pagination-page-btn"
                            onClick={() => handlePageChange(totalPages)}
                          >
                            {totalPages}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  <button
                    className="employees__pagination-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {showSuccess && (
          <div className={`employees__message ${successMessage.includes('Error') ? 'error' : 'success'}`}>
            {successMessage}
          </div>
        )}
        {showAgeDistributionModal && (
          <div className="form-overlay" onClick={() => setShowAgeDistributionModal(false)}>
            <div
              className="form-modal"
              onClick={(e) => e.stopPropagation()}
              style={{
                maxWidth: '680px',
                width: '90%',
                padding: '24px',
                borderRadius: '16px',
                backgroundColor: 'white',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1px solid #E2E8F0', paddingBottom: '12px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '700', color: '#1E293B', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <BarChart3 size={22} color="#2563EB" /> Workforce Age Distribution
                  </h3>
                  <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748B' }}>
                    Demographic age breakdown • Average Age: <strong style={{ color: '#2563EB' }}>{averageAgeStats.avg !== 'N/A' ? `${averageAgeStats.avg} years` : 'N/A'}</strong> ({averageAgeStats.count} employees)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowAgeDistributionModal(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', padding: '4px' }}
                >
                  <X size={20} />
                </button>
              </div>
              <div style={{ height: '320px', position: 'relative' }}>
                <Bar
                  data={ageDistributionChartData}
                  options={{
                    indexAxis: 'y',
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        backgroundColor: '#1E293B',
                        titleColor: '#FFFFFF',
                        bodyColor: '#F8FAFC',
                        padding: 10,
                        cornerRadius: 8,
                      }
                    },
                    scales: {
                      x: { beginAtZero: true, grid: { color: '#F1F5F9' }, ticks: { precision: 0 } },
                      y: { grid: { display: false } }
                    }
                  }}
                />
              </div>
              <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAgeDistributionModal(false)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    border: '1px solid #CBD5E1',
                    background: '#F8FAFC',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {showExportOptions && (
          <div className="form-overlay" onClick={() => setShowExportOptions(false)}>
            <div className="form-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto' }}>
              <button className="close-btn" onClick={() => setShowExportOptions(false)}><X size={20} /></button>
              <h2>Export Options</h2>
              <div className="export-columns-section">
                <h3>Select Columns to Export:</h3>
                <div className="export-columns-grid">
                  {Object.keys(exportColumns).map(key => (
                    <div key={key} className="form-group checkbox-group">
                      <label>
                        <input
                          type="checkbox"
                          checked={exportColumns[key]}
                          onChange={() => handleExportColumnToggle(key)}
                        />
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowExportOptions(false)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={exportToPDF}>Export to PDF</button>
                <button type="button" className="btn btn-primary" onClick={exportToExcel}>Export to Excel</button>
              </div>
            </div>
          </div>
        )}
        {showCreateForm && (
          <div className="form-overlay" onClick={handleCloseCreateForm}>
            <div className="form-modal" onClick={e => e.stopPropagation()}>
              <button className="close-btn" onClick={handleCloseCreateForm}><X size={20} /></button>
              <h2>{isEditingInCreateForm ? 'Edit Employee' : 'Add New Employee'}</h2>
              <form onSubmit={handleCreateFormSubmit}>
                <div className="form-section">
                  <h3>👤 Personal Information</h3>
                  <div className="form-row">
                    <div className="form-group half-width">
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        id="firstName"
                        type="text"
                        value={createFormData.firstName}
                        onChange={(e) => handleCreateFormInputChange('firstName', e.target.value)}
                        placeholder="First name"
                      />
                      {formErrors.firstName && <div className="form-error">{formErrors.firstName}</div>}
                    </div>
                    <div className="form-group half-width">
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        id="lastName"
                        type="text"
                        value={createFormData.lastName}
                        onChange={(e) => handleCreateFormInputChange('lastName', e.target.value)}
                        placeholder="Last name"
                      />
                      {formErrors.lastName && <div className="form-error">{formErrors.lastName}</div>}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group half-width">
                      <label htmlFor="dob">
                        Date of Birth *
                        {createFormData.dob && calculateAge(createFormData.dob) !== 'N/A' && (
                          <span style={{ marginLeft: '8px', fontSize: '0.8rem', color: '#64748B', fontWeight: 'normal' }}>
                            ({calculateAge(createFormData.dob)} years old)
                          </span>
                        )}
                      </label>
                      <input
                        id="dob"
                        type="date"
                        value={createFormData.dob}
                        onChange={(e) => handleCreateFormInputChange('dob', e.target.value)}
                      />
                      {formErrors.dob && <div className="form-error">{formErrors.dob}</div>}
                    </div>
                    <div className="form-group half-width">
                      <label htmlFor="gender">Gender *</label>
                      <select
                        id="gender"
                        value={createFormData.gender}
                        onChange={(e) => handleCreateFormInputChange('gender', e.target.value)}
                      >
                        <option value="">Select Gender</option>
                        {genders.filter(g => g !== 'All').map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                      {formErrors.gender && <div className="form-error">{formErrors.gender}</div>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="cin">CIN *</label>
                    <input
                      id="cin"
                      type="text"
                      value={createFormData.cin}
                      onChange={(e) => handleCreateFormInputChange('cin', e.target.value)}
                      placeholder="Enter CIN"
                    />
                    {formErrors.cin && <div className="form-error">{formErrors.cin}</div>}
                  </div>
                </div>
                <div className="form-section">
                  <h3>🔒 Account Information</h3>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      id="email"
                      type="email"
                      value={createFormData.email}
                      onChange={(e) => handleCreateFormInputChange('email', e.target.value)}
                      placeholder="Enter email"
                      required
                    />
                    {formErrors.email && <div className="form-error">{formErrors.email}</div>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="password">
                      {isEditingInCreateForm ? 'New Password (leave empty to keep current)' : 'Password *'}
                    </label>
                    <input
                      id="password"
                      type="password"
                      value={createFormData.password}
                      onChange={(e) => handleCreateFormInputChange('password', e.target.value)}
                      placeholder={isEditingInCreateForm ? "Leave empty to keep current password" : "Enter password"}
                      required={!isEditingInCreateForm}
                      minLength={isEditingInCreateForm ? 0 : 6}
                    />
                    {isEditingInCreateForm && (
                      <small className="form-help-text">
                        Leave empty to keep current password. Minimum 6 characters if changing.
                      </small>
                    )}
                    {formErrors.password && <div className="form-error">{formErrors.password}</div>}
                  </div>
                </div>
                <div className="form-section">
                  <h3>💼 Professional Information</h3>
                  <div className="employees__positions-create">
                    {createFormData.positions.map((pos, idx) => (
                      <div key={idx} className="employees__position-create-row" style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <select
                          value={pos.positionName}
                          onChange={(e) => handleCreatePositionChange(idx, 'positionName', e.target.value)}
                          className="employees__edit-select"
                          style={{ flex: 1 }}
                        >
                          <option value="">Select Position</option>
                          {POSITION_OPTIONS.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                        <select
                          value={pos.fte}
                          onChange={(e) => handleCreatePositionChange(idx, 'fte', Number(e.target.value))}
                          className="employees__edit-select"
                          style={{ width: '80px' }}
                        >
                          {FTE_OPTIONS.map(fte => (
                            <option key={fte} value={fte}>{fte}</option>
                          ))}
                        </select>
                        {createFormData.positions.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveCreatePosition(idx)}
                            className="employees__action-icon-btn employees__delete-icon-btn"
                            style={{ padding: '2px', minWidth: '24px' }}
                          >
                            <Minus size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={handleAddCreatePosition}
                      disabled={createFormData.positions.length >= 4}
                      style={{
                        background: 'linear-gradient(135deg, #1f2937 0%, #3b82f6 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        fontSize: '0.85rem',
                        fontWeight: 700,
                        cursor: createFormData.positions.length >= 4 ? 'not-allowed' : 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        boxShadow: '0 2px 4px rgba(59, 130, 246, 0.2)',
                        opacity: createFormData.positions.length >= 4 ? 0.6 : 1,
                        pointerEvents: createFormData.positions.length >= 4 ? 'none' : 'auto',
                      }}
                      onMouseEnter={(e) => {
                        if (createFormData.positions.length < 4) {
                          e.currentTarget.style.transform = 'translateY(-1px)';
                          e.currentTarget.style.boxShadow = '0 4px 8px rgba(59, 130, 246, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 4px rgba(59, 130, 246, 0.2)';
                      }}
                    >
                      <Plus size={14} />
                      Add Position
                    </button>
                    {createFormData.positions.length >= 4 && (
                      <div style={{
                        marginTop: '6px',
                        fontSize: '0.8rem',
                        color: '#6b7280',
                        fontStyle: 'italic'
                      }}>
                        Maximum of 4 positions allowed
                      </div>
                    )}
                    <div style={{ marginTop: '8px', fontSize: '0.875rem' }}>
                      Total FTE: {calculateTotalFTE(createFormData.positions).toFixed(2)}
                      {!isFTEValid(createFormData.positions) && (
                        <span style={{ color: 'red', marginLeft: '8px' }}>⚠️ Invalid</span>
                      )}
                    </div>
                    {formErrors.positions && <div className="form-error">{formErrors.positions}</div>}
                  </div>
                  <div className="form-group">
                    <label>Assigned Projects *</label>
                    <div className="employees__projects-selector-container" style={{
                      backgroundColor: '#f8fafc',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      padding: '16px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div className="search-box" style={{ position: 'relative' }}>
                        <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', pointerEvents: 'none' }} />
                        <input
                          type="text"
                          placeholder="Search projects..."
                          value={projectSearchTerm}
                          onChange={(e) => setProjectSearchTerm(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px 8px 36px',
                            borderRadius: '8px',
                            border: '1px solid #cbd5e1',
                            fontSize: '0.875rem',
                            outline: 'none',
                            backgroundColor: 'white'
                          }}
                        />
                      </div>

                      {createFormData.projects?.length > 0 && (
                        <div className="selected-chips" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', padding: '4px', borderBottom: '1px solid #f1f5f9', marginBottom: '4px' }}>
                          {createFormData.projects.map(name => {
                            const p = availableProjects.find(proj => proj.projectName === name) || { projectName: name };
                            return (
                              <div key={name} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 10px',
                                backgroundColor: '#0ea5e9',
                                color: 'white',
                                borderRadius: '20px',
                                fontSize: '0.75rem',
                                fontWeight: '600',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                              }}>
                                {name}
                                <X
                                  size={12}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    setCreateFormData(prev => {
                                      const nextProjects = (prev.projects || []).filter(pName => pName !== name);
                                      return {
                                        ...prev,
                                        projects: nextProjects,
                                        projectName: nextProjects[0] || 'N/A',
                                        secondProjectName: nextProjects.slice(1).join('-') || 'N/A'
                                      };
                                    });
                                  }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div className="projects-list" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                        gap: '8px',
                        maxHeight: '160px',
                        overflowY: 'auto',
                        padding: '4px'
                      }}>
                        {availableProjects
                          .filter(p => !projectSearchTerm || p.projectName.toLowerCase().includes(projectSearchTerm.toLowerCase()))
                          .map(project => {
                            const isSelected = createFormData.projects?.includes(project.projectName);
                            return (
                              <div
                                key={project.projectId}
                                onClick={() => {
                                  setCreateFormData(prev => {
                                    const currentProjects = prev.projects || [];
                                    const nextProjects = isSelected
                                      ? currentProjects.filter(p => p !== project.projectName)
                                      : [...currentProjects, project.projectName];
                                    return {
                                      ...prev,
                                      projects: nextProjects,
                                      projectName: nextProjects[0] || 'N/A',
                                      secondProjectName: nextProjects.slice(1).join('-') || 'N/A'
                                    };
                                  });
                                }}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '8px 12px',
                                  borderRadius: '10px',
                                  border: '1px solid',
                                  borderColor: isSelected ? '#0ea5e9' : '#e2e8f0',
                                  backgroundColor: isSelected ? '#f0f9ff' : '#ffffff',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  gap: '8px',
                                  boxShadow: isSelected ? '0 1px 3px rgba(14, 165, 233, 0.15)' : 'none'
                                }}
                              >
                                <div style={{
                                  width: '16px',
                                  height: '16px',
                                  borderRadius: '4px',
                                  border: '1px solid',
                                  borderColor: isSelected ? '#0ea5e9' : '#cbd5e1',
                                  backgroundColor: isSelected ? '#0ea5e9' : 'transparent',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'white'
                                }}>
                                  {isSelected && <Plus size={12} style={{ transform: 'rotate(45deg)' }} />}
                                </div>
                                <span style={{
                                  fontSize: '0.8rem',
                                  fontWeight: isSelected ? '600' : '400',
                                  color: isSelected ? '#0369a1' : '#475569',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {project.projectName}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                    {availableProjects.length === 0 && (
                      <div style={{ fontSize: '0.875rem', color: '#64748b', fontStyle: 'italic', marginTop: '4px' }}>
                        No projects found. Please create projects first.
                      </div>
                    )}
                  </div>
                  <div className="form-row">
                    <div className="form-group half-width">
                      <label htmlFor="departmentName">Department *</label>
                      <select
                        id="departmentName"
                        value={createFormData.departmentName}
                        onChange={(e) => handleCreateFormInputChange('departmentName', e.target.value)}
                      >
                        <option value="">Select Department</option>
                        {departments
                          .filter(d => d !== 'All')
                          .map(dept => (
                            <option key={dept} value={dept}>
                              {dept === 'Customer_services' ? 'Customer Services' : dept}
                            </option>
                          ))}
                      </select>
                      {formErrors.departmentName && <div className="form-error">{formErrors.departmentName}</div>}
                    </div>
                    <div className="form-group half-width">
                      <label htmlFor="dateOfJoining">Joining Date *</label>
                      <input
                        id="dateOfJoining"
                        type="date"
                        value={createFormData.dateOfJoining}
                        onChange={(e) => handleCreateFormInputChange('dateOfJoining', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                      />
                      {formErrors.dateOfJoining && <div className="form-error">{formErrors.dateOfJoining}</div>}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group half-width">
                      <label htmlFor="office">Office *</label>
                      <select
                        id="office"
                        value={createFormData.office}
                        onChange={(e) => handleCreateFormInputChange('office', e.target.value)}
                      >
                        <option value="">Select Office</option>
                        {offices.filter(o => o !== 'All').map(office => <option key={office} value={office}>{office}</option>)}
                      </select>
                      {formErrors.office && <div className="form-error">{formErrors.office}</div>}
                    </div>
                    <div className="form-group half-width">
                      <label htmlFor="phone">Phone Number *</label>
                      <input
                        id="phone"
                        type="text"
                        value={createFormData.phone}
                        onChange={(e) => handleCreateFormInputChange('phone', e.target.value)}
                        placeholder="Enter phone number"
                      />
                      {formErrors.phone && <div className="form-error">{formErrors.phone}</div>}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group half-width">
                      <label htmlFor="company">Company *</label>
                      <select
                        id="company"
                        value={createFormData.company}
                        onChange={(e) => handleCreateFormInputChange('company', e.target.value)}
                      >
                        <option value="">Select Company</option>
                        {companyOptions.map(comp => <option key={comp} value={comp}>{comp}</option>)}
                      </select>
                      {formErrors.company && <div className="form-error">{formErrors.company}</div>}
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="cnss">CNSS</label>
                    <input
                      id="cnss"
                      type="text"
                      value={createFormData.cnss}
                      onChange={(e) => handleCreateFormInputChange('cnss', e.target.value)}
                      placeholder="Enter CNSS number"
                    />
                    {formErrors.cnss && <div className="form-error">{formErrors.cnss}</div>}
                  </div>
                  <div className="form-group">
                    <label htmlFor="role">Role *</label>
                    <select
                      id="role"
                      value={createFormData.role}
                      onChange={(e) => handleCreateFormInputChange('role', e.target.value)}
                    >
                      <option value="">Select Role</option>
                      {roles.map(role => <option key={role} value={role}>{role}</option>)}
                    </select>
                    {formErrors.role && <div className="form-error">{formErrors.role}</div>}
                  </div>
                </div>
                <div className="form-section">
                  <h3>🎓 Diploma</h3>
                  <div className="form-group">
                    <label htmlFor="diploma">Education Level *</label>
                    <select
                      id="diploma"
                      value={createFormData.diploma}
                      onChange={(e) => handleCreateFormInputChange('diploma', e.target.value)}
                    >
                      <option value="">Select Education Level</option>
                      {diplomaOptions.map(diploma => <option key={diploma} value={diploma}>{diploma}</option>)}
                    </select>
                    {formErrors.diploma && <div className="form-error">{formErrors.diploma}</div>}
                  </div>
                </div>
                <div className="form-section">
                  <h3>📷 Profile Image</h3>
                  <div className="image-upload-container">
                    <div className="image-preview">
                      {createFormData.profileImage ? (
                        <img
                          src={newImageUploaded ? `data:image/jpeg;base64,${createFormData.profileImage}` : createFormData.profileImage}
                          alt="Preview"
                          className="image-preview-img"
                        />
                      ) : (
                        <div className="image-placeholder"><User size={40} /><span>No image selected</span></div>
                      )}
                    </div>
                    <input type="file" accept="image/*" onChange={handleCreateFormImageUpload} id="image-upload" className="image-input" />
                    <label htmlFor="image-upload" className="image-label">
                      {createFormData.profileImage ? 'Change Image' : 'Upload Image'}
                    </label>
                  </div>
                  {formErrors.profileImage && <div className="form-error">{formErrors.profileImage}</div>}
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseCreateForm}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={Object.values(formErrors).some(error => error)}>
                    {isEditingInCreateForm ? 'Update Employee' : 'Add Employee'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Feedback Report Modal */}
        {showFeedbackReport && feedbackReportEmployee && (
          <ReportChooserModal
            employee={feedbackReportEmployee}
            onClose={handleCloseFeedbackReport}
          />
        )}

        {/* Full Feedback Report Overlay */}
        {showFullFeedbackReport && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}>
            <div style={{
              backgroundColor: '#F8FAFC',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '95vw', // Use 95% of viewport width
              height: '95vh',   // Use 95% of viewport height
              overflow: 'hidden', // Prevent outer scroll
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}>
              {/* Header with Close Button */}
              <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                padding: '10px 20px',
                backgroundColor: 'white',
                borderBottom: '1px solid #E2E8F0'
              }}>
                <button
                  onClick={() => setShowFullFeedbackReport(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#64748B',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '0.9rem',
                    fontWeight: 600
                  }}
                >
                  Close <X size={20} />
                </button>
              </div>

              {/* Content Area - Scrollable */}
              <div style={{
                flex: 1,
                overflow: 'auto',
                // We overwrite the internal container style of EmployeeFeedbackReport to fit
              }}>
                <div style={{ minWidth: '1200px' }}>
                  <EmployeeFeedbackReportContent isDialog={false} />
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

const ReportChooserModal = ({ employee, onClose }) => {
  const [selectedReport, setSelectedReport] = useState(null);

  if (selectedReport === 'FEEDBACK') {
    return <FeedbackReportModal employee={employee} onClose={onClose} />;
  }

  if (selectedReport === 'ABSENCE') {
    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000, padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white', borderRadius: '16px', width: '100%', maxWidth: '900px', maxHeight: '90vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)', padding: '16px 24px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <h2 style={{ color: 'white', margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Absence Report - {employee.firstName} {employee.lastName}</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer', display: 'flex', padding: '4px' }}><X size={24} /></button>
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: '0px' }}>
            <SimpleAbsenceReportContent employee={employee} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 10000
    }}>
      <div style={{ backgroundColor: 'white', padding: '32px', borderRadius: '16px', width: '100%', maxWidth: '400px', textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
        <h2 style={{ marginBottom: '24px', color: '#1E293B', fontWeight: 700 }}>Select Report Type</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <button
            onClick={() => setSelectedReport('FEEDBACK')}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            style={{ padding: '16px', borderRadius: '12px', border: '1px solid #BFDBFE', background: '#EFF6FF', color: '#1D4ED8', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', fontSize: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
          >
            Feedback Report
          </button>
          <button
            onClick={() => setSelectedReport('ABSENCE')}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
            style={{ padding: '16px', borderRadius: '12px', border: '1px solid #BBF7D0', background: '#F0FDF4', color: '#15803D', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s', fontSize: '1rem', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}
          >
            Absence Report
          </button>
        </div>
        <button onClick={onClose} style={{ marginTop: '24px', background: 'none', border: 'none', color: '#64748B', cursor: 'pointer', textDecoration: 'underline', fontWeight: 600 }}>
          Cancel
        </button>
      </div>
    </div>
  );
};

// Feedback Report Modal Component
const FeedbackReportModal = ({ employee, onClose }) => {
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState('All');
  const [expandedTypes, setExpandedTypes] = useState({});
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' for newest first, 'asc' for oldest first
  const [empImage, setEmpImage] = useState(null);

  useEffect(() => {
    const fetchImage = async () => {
      try {
        const empId = employee.employeeId || employee.id;
        const imgBlob = await getEmployeeImage(empId);
        if (imgBlob) setEmpImage(imgBlob);
      } catch (e) {
        console.warn('Failed to fetch image in feedback report', e);
      }
    };
    fetchImage();
  }, [employee]);

  useEffect(() => {
    const fetchFeedbackHistory = async () => {
      setLoading(true);
      try {
        const empId = employee.employeeId || employee.id;
        const notes = await getEmployeeNotes(empId);

        // Filter and format notes
        const formattedNotes = notes
          .filter(note => note.incidentDate) // Only show structured feedback
          .map(note => ({
            date: note.incidentDate,
            type: note.violationType || 'N/A',
            description: note.description || 'N/A',
            managerReview: note.managerReview || 'N/A',
            employeeReaction: note.employeeReaction || 'N/A',
            employeeAgreement: note.employeeAgreement || 'N/A',
            noteType: note.noteType || 'NEGATIVE',
            createdDate: note.createdDate,
            createdBy: note.createdBy || 'N/A'
          }))
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        setFeedbackHistory(formattedNotes);
      } catch (error) {
        console.error('Error fetching feedback history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchFeedbackHistory();
  }, [employee]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (err) {
      return 'Invalid Date';
    }
  };



  const getViolationStyle = (type) => {
    if (type.includes('(Positive)')) {
      return { bg: '#DCFCE7', color: '#166534', border: '#BBF7D0' }; // Light green theme
    }
    const styles = {
      'Violation of Rules': { bg: '#FEE2E2', color: '#991B1B', border: '#FECACA' },
      'Lateness': { bg: '#FFF7ED', color: '#9A3412', border: '#FED7AA' },
      'Sickness': { bg: '#FEF2F2', color: '#991B1B', border: '#FEE2E2' },
      'Personal': { bg: '#EFF6FF', color: '#1E40AF', border: '#BFDBFE' },
      'Personal Commitment': { bg: '#EFF6FF', color: '#1E40AF', border: '#BFDBFE' }
    };
    return styles[type] || { bg: '#F3F4F6', color: '#1F2937', border: '#E5E7EB' };
  };

  const filteredHistory = feedbackHistory
    .filter(item => {
      const itemDate = new Date(item.date);
      const matchYear = itemDate.getFullYear() === selectedYear;
      const matchMonth = selectedMonth === 'All' || itemDate.getMonth() === parseInt(selectedMonth);
      return matchYear && matchMonth;
    })
    .sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
    });

  // Group records by type (separate visual groups for positive ones)
  const groupedByType = filteredHistory.reduce((acc, item) => {
    const key = item.noteType === 'POSITIVE' ? `${item.type} (Positive)` : item.type;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});

  const toggleTypeExpansion = (type) => {
    setExpandedTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];

  const handleExportPDF = async () => {
    if (filteredHistory.length === 0) {
      alert("No feedback records to export.");
      return;
    }

    // Add a delay for image loading as requested
    await new Promise(resolve => setTimeout(resolve, 1500));

    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      const margin = 14;
      const pw = doc.internal.pageSize.width;

      const marabesBase64 = await getBase64FromUrl(marabesLogo);
      const axoBase64 = await getBase64FromUrl('/cLogo.png');

      const getImageSize = (base64Data, maxH) => {
        return new Promise((resolve) => {
          if (!base64Data) { resolve({ w: 30, h: maxH }); return; }
          const img = new Image();
          img.onload = () => {
            const ratio = img.width / img.height;
            resolve({ w: maxH * ratio, h: maxH });
          };
          img.onerror = () => resolve({ w: 30, h: maxH });
          img.src = base64Data;
        });
      };

      const marabesSize = await getImageSize(marabesBase64, 10);
      const axoSize = await getImageSize(axoBase64, 10);

      let employeeBase64 = null;
      try {
        const empId = employee.employeeId || employee.id;
        const imgUrl = empImage || await getEmployeeImage(empId);

        if (imgUrl) {
          // Fetch the blob directly to avoid any Object URL loading quirks in the Image element
          const res = await fetch(imgUrl);
          const blob = await res.blob();

          // Convert to base64 first
          const base64Data = await new Promise((res) => {
            const reader = new FileReader();
            reader.onloadend = () => res(reader.result);
            reader.readAsDataURL(blob);
          });

          // Draw and crop on canvas
          employeeBase64 = await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              const size = 150;
              canvas.width = size;
              canvas.height = size;
              const ctx = canvas.getContext('2d');

              ctx.fillStyle = '#F8FAFC';
              ctx.fillRect(0, 0, size, size);

              ctx.beginPath();
              ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
              ctx.closePath();
              ctx.clip();

              const scale = Math.max(size / img.width, size / img.height);
              const x = (size - img.width * scale) / 2;
              const y = (size - img.height * scale) / 2;

              ctx.drawImage(img, x, y, img.width * scale, img.height * scale);

              ctx.lineWidth = 4;
              ctx.strokeStyle = '#E2E8F0';
              ctx.stroke();

              resolve(canvas.toDataURL('image/jpeg', 0.8));
            };
            img.onerror = () => {
              resolve(null);
            };
            img.src = base64Data;
          });
        }
      } catch (e) {
        console.warn("Failed to convert employee image to base64 for PDF", e);
      }

      if (marabesBase64) doc.addImage(marabesBase64, 'PNG', margin, 10, marabesSize.w, marabesSize.h);
      if (axoBase64) doc.addImage(axoBase64, 'PNG', pw - margin - axoSize.w, 10, axoSize.w, axoSize.h);

      // Draw a subtle background for the header
      doc.setFillColor(248, 250, 252); // #F8FAFC
      doc.roundedRect(margin, 26, pw - margin * 2, 26, 2, 2, 'F');
      doc.setDrawColor(226, 232, 240); // #E2E8F0
      doc.roundedRect(margin, 26, pw - margin * 2, 26, 2, 2, 'S');

      if (employeeBase64) {
        // Place image on the right side of the box
        doc.addImage(employeeBase64, 'JPEG', pw - margin - 24, 29, 20, 20);
      } else {
        // Fallback to initials if no image is available
        doc.setFillColor(226, 232, 240); // #E2E8F0
        doc.circle(pw - margin - 14, 39, 10, 'F');
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(100, 116, 139); // #64748B
        const initials = (employee.firstName?.charAt(0) || '') + (employee.lastName?.charAt(0) || '');
        doc.text(initials, pw - margin - 14, 40, { align: 'center', baseline: 'middle' });
      }

      const textStartX = margin + 6;

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(30, 41, 59);
      doc.text(`Feedback History - ${employee.firstName} ${employee.lastName}`, textStartX, 34);

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(employee.email || 'N/A', textStartX, 39);

      const empOffice = typeof employee.office === 'object' ? employee.office?.name || 'N/A' : employee.office || 'N/A';
      const empPosition = employee.positions ? employee.positions.map(p => p.positionName).filter(Boolean).join(', ') : 'N/A';
      const empProjects = employee.projects && employee.projects.length > 0 ? employee.projects.map(p => typeof p === 'string' ? p : p.projectName).join(', ') : [employee.projectName, employee.secondProjectName].filter(p => p && p !== 'N/A').join(', ') || 'N/A';

      doc.setFontSize(8);
      doc.setTextColor(51, 65, 85);

      // Row 1
      doc.text(`Start date: ${formatDate(employee.dateOfJoining)}`, textStartX, 45);
      doc.text(`Projects: ${empProjects}`, textStartX + 50, 45);
      doc.text(`Age : ${calculateAge(employee.dob)}`, textStartX + 110, 45);

      // Row 2
      doc.text(`Office: ${empOffice}`, textStartX, 49);
      doc.text(`Positions : ${empPosition}`, textStartX + 50, 49);

      // Generated Date moved above the box on the right
      doc.setFontSize(8);
      doc.setTextColor(148, 163, 184);
      doc.text(`Generated: ${format(new Date(), 'dd-MM-yyyy HH:mm')}`, pw - margin, 24, { align: 'right' });

      const headers = [['Date', 'Type', 'Description', 'Manager Review', 'Employee Reaction', 'Agreement']];
      const body = filteredHistory.map(fb => [
        formatDate(fb.date),
        fb.noteType === 'POSITIVE' ? `${fb.type} (Positive)` : fb.type,
        fb.description,
        fb.managerReview,
        fb.employeeReaction,
        fb.employeeAgreement
      ]);

      autoTable(doc, {
        startY: 56,
        head: headers,
        body: body,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 3 },
        headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontStyle: 'bold' },
        didParseCell: function (data) {
          if (data.section === 'body' && data.column.index === 1) {
            const cellValue = data.cell.raw || '';
            if (typeof cellValue === 'string' && cellValue.includes('(Positive)')) {
              data.cell.styles.fillColor = [220, 252, 231]; // #DCFCE7
              data.cell.styles.textColor = [22, 101, 52]; // #166534
              data.cell.styles.fontStyle = 'bold';
            }
          }
        }
      });

      doc.save(`Feedback_History_${employee.firstName}_${employee.lastName}_${format(new Date(), 'dd-MM-yyyy')}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF.');
    }
  };

  const handleExportExcel = () => {
    if (filteredHistory.length === 0) {
      alert("No feedback records to export.");
      return;
    }

    const rows = filteredHistory.map(fb => ({
      'Date': formatDate(fb.date),
      'Type': fb.type,
      'Description': fb.description,
      'Manager Review': fb.managerReview,
      'Employee Reaction': fb.employeeReaction,
      'Employee Agreement': fb.employeeAgreement
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Feedback History');
    XLSX.writeFile(wb, `Feedback_History_${employee.firstName}_${employee.lastName}_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '20px'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        width: '100%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #1E293B 0%, #334155 100%)',
          padding: '20px 24px',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '60px', height: '60px', borderRadius: '50%', overflow: 'hidden', border: '2px solid rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1.5rem', fontWeight: 'bold' }}>
              {empImage ? <img src={empImage} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (employee.firstName?.charAt(0) || '') + (employee.lastName?.charAt(0) || '')}
            </div>
            <div>
              <h2 style={{ color: 'white', fontSize: '1.5rem', fontWeight: '700', margin: 0 }}>
                Feedback History
              </h2>
              <p style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.9rem', margin: '4px 0 8px 0' }}>
                {employee.firstName} {employee.lastName} - {employee.email}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.9)', marginTop: '8px' }}>
                {/* Row 1 */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(0,0,0,0.2)', padding: '4px 8px', borderRadius: '12px' }}>
                    <strong>Start date:</strong> {formatDate(employee.dateOfJoining)}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(0,0,0,0.2)', padding: '4px 8px', borderRadius: '12px' }}>
                    <strong>Projects:</strong> {employee.projects && employee.projects.length > 0 ? employee.projects.map(p => typeof p === 'string' ? p : p.projectName).join(' - ') : [employee.projectName, employee.secondProjectName].filter(p => p && p !== 'N/A').join(' - ') || 'N/A'}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(0,0,0,0.2)', padding: '4px 8px', borderRadius: '12px' }}>
                    <strong>Age :</strong> {calculateAge(employee.dob)}
                  </span>
                </div>
                {/* Row 2 */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(0,0,0,0.2)', padding: '4px 8px', borderRadius: '12px' }}>
                    <strong>Office:</strong> {typeof employee.office === 'object' ? employee.office?.name || 'N/A' : employee.office || 'N/A'}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'rgba(0,0,0,0.2)', padding: '4px 8px', borderRadius: '12px' }}>
                    <strong>Positions :</strong> {employee.positions ? employee.positions.map(p => p.positionName).filter(Boolean).join(', ') : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <button
              onClick={handleExportPDF}
              title="Export to PDF"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            >
              <FileText size={20} />
            </button>
            <button
              onClick={handleExportExcel}
              title="Export to Excel"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
            >
              <FileSpreadsheet size={20} />
            </button>
            <div style={{ width: '1px', height: '24px', backgroundColor: 'rgba(255,255,255,0.2)', margin: '0 4px' }}></div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div style={{
          padding: '16px 24px',
          backgroundColor: '#F8FAFC',
          borderBottom: '1px solid #E2E8F0',
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          flexWrap: 'wrap'
        }}>
          <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            style={{
              padding: '6px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '0.875rem',
              backgroundColor: 'white'
            }}
          >
            <option value={new Date().getFullYear()}>{new Date().getFullYear()}</option>
            <option value={new Date().getFullYear() - 1}>{new Date().getFullYear() - 1}</option>
            <option value={new Date().getFullYear() - 2}>{new Date().getFullYear() - 2}</option>
          </select>

          <label style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151', marginLeft: '12px' }}>Month:</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '0.875rem',
              backgroundColor: 'white'
            }}
          >
            <option value="All">All Months</option>
            {monthNames.map((month, idx) => (
              <option key={idx} value={idx.toString()}>{month}</option>
            ))}
          </select>

          <button
            onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
            style={{
              padding: '6px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '8px',
              fontSize: '0.875rem',
              backgroundColor: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontWeight: '500',
              color: '#374151',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
          >
            {sortOrder === 'desc' ? <ArrowDownNarrowWide size={16} /> : <ArrowUpNarrowWide size={16} />}
            {sortOrder === 'desc' ? 'Newest First' : 'Oldest First'}
          </button>

          <div style={{ flex: 1 }} />
          <span style={{ fontSize: '0.875rem', color: '#6B7280', fontWeight: '600' }}>
            {filteredHistory.length} record{filteredHistory.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
              Loading feedback history...
            </div>
          ) : filteredHistory.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>
              <BarChart3 size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
              <p>No feedback records found for this period.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {Object.entries(groupedByType).map(([type, records]) => {
                const violationStyle = getViolationStyle(type);
                const isExpanded = expandedTypes[type];

                return (
                  <div key={type} style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    backgroundColor: 'white'
                  }}>
                    {/* Type Header - Clickable to expand/collapse */}
                    <div
                      onClick={() => toggleTypeExpansion(type)}
                      style={{
                        padding: '16px 20px',
                        backgroundColor: violationStyle.bg,
                        borderBottom: isExpanded ? '1px solid #E5E7EB' : 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                          backgroundColor: violationStyle.color,
                          color: 'white',
                          padding: '6px 16px',
                          borderRadius: '9999px',
                          fontSize: '0.875rem',
                          fontWeight: '700',
                          border: `1px solid ${violationStyle.border}`
                        }}>
                          {type}
                        </span>
                        <span style={{
                          fontSize: '0.875rem',
                          fontWeight: '600',
                          color: violationStyle.color
                        }}>
                          {records.length} record{records.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <ChevronRight
                        size={20}
                        style={{
                          color: violationStyle.color,
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }}
                      />
                    </div>

                    {/* Expanded Records Table */}
                    {isExpanded && (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#F9FAFB' }}>
                              <th style={{ ...tableHeaderStyle, minWidth: '100px' }}>Date</th>
                              <th style={{ ...tableHeaderStyle, minWidth: '250px' }}>Description</th>
                              <th style={{ ...tableHeaderStyle, minWidth: '150px' }}>Manager Review</th>
                              <th style={{ ...tableHeaderStyle, minWidth: '250px' }}>Employee Reaction</th>
                              <th style={{ ...tableHeaderStyle, minWidth: '200px' }}>Employee Agreement</th>
                            </tr>
                          </thead>
                          <tbody>
                            {records.map((item, idx) => (
                              <tr key={idx} style={{
                                borderBottom: idx < records.length - 1 ? '1px solid #E5E7EB' : 'none',
                                transition: 'background-color 0.2s'
                              }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              >
                                <td style={{ ...tableCellStyle, minWidth: '100px' }}>{formatDate(item.date)}</td>
                                <td style={{ ...tableCellStyle, minWidth: '250px', whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: item.description }} />
                                <td style={{ ...tableCellStyle, minWidth: '150px', whiteSpace: 'pre-wrap' }}>{item.managerReview}</td>
                                <td style={{ ...tableCellStyle, minWidth: '250px', whiteSpace: 'pre-wrap' }}>{item.employeeReaction}</td>
                                <td style={{ ...tableCellStyle, minWidth: '200px', whiteSpace: 'pre-wrap' }}>{item.employeeAgreement}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const tableHeaderStyle = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#1E293B',
  textTransform: 'uppercase',
  borderBottom: '2px solid #E2E8F0',
  position: 'sticky',
  top: 0,
  backgroundColor: '#F1F5F9',
  zIndex: 10
};

const tableCellStyle = {
  padding: '12px 16px',
  fontSize: '0.875rem',
  color: '#334155',
  verticalAlign: 'top'
};

export default Employees;
